import { Type } from "@google/genai";
import mammoth from 'mammoth';

import { supabase, supabase_get_client_settings } from './supabaseService';
import { normalizeCvAnalysisResult } from '../utils/cvParseNormalize';



// ---------------------------------------------------------------------------
// AI PROXY CLIENT (Azure OpenAI only — server uses deployment names from env, not these ids)
// All AI calls go through POST /api/gemini/generate and /api/gemini/embed.
// See server/azureOpenai.cjs and server/.env (AZURE_OPENAI_DEPLOYMENT_CHAT, AZURE_OPENAI_DEPLOYMENT_EMBEDDINGS).
// ---------------------------------------------------------------------------

/** Placeholder id in JSON bodies; real model is AZURE_OPENAI_DEPLOYMENT_* on the server */
export const AI_PROXY_CHAT_MODEL = "azure-openai-chat";
export const AI_PROXY_EMBED_MODEL = "azure-openai-embed";

const GEMINI_PROXY_BASE = (import.meta as any).env?.VITE_API_URL ||
    (typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? '' : 'http://localhost:4000');

const proxyGenerateContent = async (params: { model: string; contents: any; config?: any; systemInstruction?: any }) => {
  let contents = params.contents;
  if (typeof contents === 'string') {
      contents = [{ role: 'user', parts: [{ text: contents }] }];
  } else if (Array.isArray(contents)) {
      contents = contents.map(c => ({
          role: c.role || 'user',
          parts: Array.isArray(c.parts) ? c.parts : [{ text: c.parts || c.text || '' }]
      }));
  } else {
      contents = [contents];
  }

  // Handle system instruction explicitly for the REST API
  let systemInstructionPayload: any = undefined;
  if (params.systemInstruction) {
      systemInstructionPayload = {
          parts: [{ text: typeof params.systemInstruction === 'string' ? params.systemInstruction : JSON.stringify(params.systemInstruction) }]
      };
  }

  const controller = new AbortController();
  const timeoutMs =
      (params?.config && typeof (params.config as any).timeoutMs === 'number' && (params.config as any).timeoutMs > 0)
          ? (params.config as any).timeoutMs
          : 60000; // default 60s hard timeout
  const timeoutId = setTimeout(() => controller.abort(new Error('Timeout: AI-server reageert niet (probeer opnieuw).')), timeoutMs);

  try {
      const res = await fetch(`${GEMINI_PROXY_BASE}/api/gemini/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            model: params.model,
            contents, 
            config: params.config,
            systemInstruction: systemInstructionPayload
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        const errText = await res.text();
        let msg = `AI proxy error ${res.status}`;
        try { const parsed = JSON.parse(errText); if (parsed.error?.message) msg = parsed.error.message; else if (parsed.error) msg = parsed.error; } catch(e) {}
        throw new Error(msg);
      }
      
      const data = await res.json();
      
      return {
        text: data.text || '',
        usageMetadata: data.usageMetadata || null,
        response: data
      };
  } catch (e: any) {
      clearTimeout(timeoutId);
      throw e;
  }
};

const proxyEmbedContent = async (params: { model: string; contents: any; config?: any }) => {
  const res = await fetch(`${GEMINI_PROXY_BASE}/api/gemini/embed`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: params.model,
      contents: params.contents,
      config: params.config
    })
  });
  
  if (!res.ok) {
    const errText = await res.text();
    let msg = `AI proxy embed error ${res.status}`;
    try { const parsed = JSON.parse(errText); if (parsed.error?.message) msg = parsed.error.message; else if (parsed.error) msg = parsed.error; } catch(e) {}
    throw new Error(msg);
  }
  
  const data = await res.json();
  return { embeddings: data.embeddings || [] };
};

export const ai = {
  models: {
    generateContent: proxyGenerateContent,
    embedContent: proxyEmbedContent
  }
} as any;

// ---------------------------------------------------------------------------
// Token Usage Tracking (silent, non-blocking)
// ---------------------------------------------------------------------------
const originalGenerateContent = proxyGenerateContent;
(ai.models as any).generateContent = async (params: any) => {
  const response = await originalGenerateContent(params);
  try {
    const usage = response.usageMetadata;
    if (usage && (usage.promptTokenCount || usage.candidatesTokenCount)) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          // Token tracking backend function could be called here
        }
      });
    }
  } catch (e) {
    console.warn("Silent token tracking hook failed:", e);
  }
  return response;
};

const CLEAN_TEXT_INSTRUCTION = "Gebruik absoluut GEEN Markdown-opmaak zoals hekjes (####), sterretjes (**), of underscores (_). Lever de tekst op als een professioneel, schoon document zonder technische symbolen.";

const FROUKJE_PROMPT = "Je bent Froukje, een gevatte, slimme en blondharige AI Recruiter van Werkplaats. Je praat in de 'je' vorm, bent professioneel maar toegankelijk en gebruikt af en toe een vleugje recruitment-humor of scherpe observaties.";

/**
 * Utility to ensure we parse JSON safely even if the model returns markdown blocks
 */
const parseGeminiJson = (text: string) => {
  try {
    if (!text) throw new Error("Lege response van AI");

    // 1. Remove markdown code blocks (standard ```json ... ```)
    let cleaned = text.replace(/```json/gi, '').replace(/```/g, '');

    // 2. Find the FIRST open brace/bracket and LAST close brace/bracket
    const firstBrace = cleaned.indexOf('{');
    const firstBracket = cleaned.indexOf('[');

    let startIndex = -1;

    // Determine if we are looking for an object or an array
    if (firstBrace !== -1 && (firstBracket === -1 || firstBrace < firstBracket)) {
      startIndex = firstBrace;
    } else if (firstBracket !== -1) {
      startIndex = firstBracket;
    }

    if (startIndex !== -1) {
      // Extract strictly from the start of the JSON
      cleaned = cleaned.substring(startIndex);
    }

    // 3. Cleanup whitespace
    cleaned = cleaned.trim();

    try {
      return JSON.parse(cleaned);
    } catch (parseErr) {
      // Fallback: try to aggressively repair unterminated JSON by appending closing brackets
      console.warn("JSON Parse failed, attempting aggressive repair...", parseErr);
      const repairAttempts = [
        cleaned + '"}',
        cleaned + '"]}',
        cleaned + '"}]}',
        cleaned + '}]}',
        cleaned + ']}',
        cleaned + '}',
        cleaned + ']'
      ];

      for (const attempt of repairAttempts) {
        try {
          return JSON.parse(attempt);
        } catch (e) {
          // Ignore and try next
        }
      }
      throw parseErr; // Throw original error if all repairs fail
    }

  } catch (e) {
    console.error("JSON Parse Error. Raw text:", text);
    throw new Error("Ongeldige JSON ontvangen van AI. Probeer het opnieuw.");
  }
};

/**
 * Generates a 768-D Vector Embedding for semantic search using Google GenAI
 */
export const generateEmbedding = async (text: string): Promise<number[] | null> => {
  if (!text || text.trim().length === 0) return null;
  try {
    const response = await ai.models.embedContent({
      model: AI_PROXY_EMBED_MODEL,
      contents: text.substring(0, 9500), // Keep chunk safe within embedding limits
      config: {
          outputDimensionality: 768
      }
    });
    return response.embeddings?.[0]?.values || null;
  } catch (error: any) {
    console.error("Failed to generate vector embedding:", error);
    if (error?.message?.includes('429') || error?.message?.includes('Resource exhausted') || error?.message?.includes('Too Many Requests')) {
        throw new Error('429_TOO_MANY_REQUESTS');
    }
    return null;
  }
};


/**
 * Parses raw vacancy text to extract structured data
 */
export const parseVacancyText = async (text: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Analyseer de volgende vacaturetekst en extraheer de kerngegevens.
    
    TEKST:
    "${text.substring(0, 10000)}"

    Output JSON:
    {
      "title": "Functietitel",
      "category": "Categorie (bijv. IT, Sales, Zorg, Finance, HR)",
      "summary": "Korte samenvatting van de rol (max 3 zinnen)",
      "keywords": "Comma separated string of top 5 skills/keywords"
    }`,
  config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          category: { type: Type.STRING },
          summary: { type: Type.STRING },
          keywords: { type: Type.STRING }
        },
        required: ["title", "category"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

/**
 * Parses raw tender/aanbesteding text to extract structured data
 */
export const parseTenderText = async (text: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Analyseer de volgende aanbesteding/tender tekst en extraheer de kerngegevens.
    
    TEKST:
    "${text.substring(0, 15000)}"

    Output JSON:
    {
      "title": "Titel van de aanbesteding (vaak de rol of het project)",
      "client": "Naam van de klant of instantie (bijv. Ministerie van X, Gemeente Y)",
      "summary": "Korte samenvatting van de aanvraag (max 3 of 4 zinnen)",
      "requirements": ["Harde eis 1", "Harde eis 2"],
      "deadline": "Deadline datum in YYYY-MM-DD formaat indien gevonden, anders null"
    }`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          client: { type: Type.STRING },
          summary: { type: Type.STRING },
          requirements: { type: Type.ARRAY, items: { type: Type.STRING } },
          deadline: { type: Type.STRING }
        },
        required: ["title", "client", "summary", "requirements"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

/**
 * Generates or improves a WhatsApp message designed for candidate outreach.
 */
export const improveWhatsAppMessage = async (text: string, candidateName: string, context?: string) => {
  const prompt = `Je bent Froukje, een professionele maar vlot communicerende Recruitment AI.
  Schrijf of verbeter een kort, krachtig WhatsApp-bericht voor kandidaat "${candidateName}".
  
  HUIDIGE TEKST OF INSTRUCTIE: 
  "${text}"
  
  CONTEXT (bijv. vacaturetitel of tender):
  "${context || 'Geen specifieke context'}"
  
  Instructies:
  - Houd het informeel maar respectvol (je/jij).
  - Maak het niet te lang (max 3-4 korte zinnen), wegens WhatsApp.
  - Sluit af met een open vraag om antwoord uit te lokken.
  - GEEN placeholders zoals [Titel], verzin iets passends als er info mist of gebruik de context.
  
  Geef ALLEEN de uiteindelijke WhatsApp tekst terug zonder aanhalingstekens of uitleg.`;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: prompt,
  });
  return response.text.trim();
};

/**
 * Performs a batch AI match for a set of candidates against a tender.
 */
export const matchTenderCandidates = async (tender: any, candidates: any[]) => {
  const candidateSummaries = candidates.map(c => {
    return `ID: ${c.id}\nNaam: ${c.candidate?.name || 'Onbekend'}\nProfiel: ${c.ai_parsed_data?.summary || c.candidate?.cv_summary || ''}\nSkills: ${c.ai_parsed_data?.skills?.join(', ') || ''}\n---`;
  }).join('\n');

  const prompt = `Je krijgt een tender/aanbesteding en een lijst met kandidaten.
Titel: ${tender.title}
Eisen: ${tender.requirements?.join(', ') || tender.summary}

Kandidaten:
${candidateSummaries}

Evalueer elke kandidaat tegenover deze tender.
Geef een JSON array van objecten terug, waar elk object heeft:
- "candidateId": (string) De Exacte ID van de kandidaat.
- "score": (number) Een eerlijke rating (0 tot 100) hoe goed ze passen.
- "rationale": (string) Eén korte zin (max 15 woorden) met de reden waarom.
`;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            candidateId: { type: Type.STRING },
            score: { type: Type.NUMBER },
            rationale: { type: Type.STRING }
          },
          required: ["candidateId", "score", "rationale"]
        }
      }
    }
  });

  return parseGeminiJson(response.text);
};

/**
 * Generates a full website section based on user prompt
 */
export const generateWebsiteSection = async (prompt: string, type: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Expert Web Designer (Elementor Style). Maak een nieuwe website sectie.
    TYPE: "${type}"
    GEBRUIKERSWENS: "${prompt}"

    Output a SINGLE SECTION object matching this strict structure:

    {
      "type": "custom",
      "metadata": { "name": "...", "category": "...", "purpose": "..." },
      "layout": { 
        "containerType": "boxed",
        "columns": 1,
        "columnRatios": [100], 
        "verticalAlignment": "middle",
        "gap": "medium"
      },
      "style_controls": { "colors": { "background": "#fff", "text": "#000" }, "padding": "py-20" },
      "elements": [
        {
          "type": "heading",
          "columnIndex": 0,
          "content": { "text": "...", "level": "h2" },
          "style": { ... }
        }
      ]
    }
    
    Allowed elements: heading, text, rich_text, image, button, icon_list, card, stats_counter.
    `,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          type: { type: Type.STRING },
          metadata: {
            type: Type.OBJECT,
            properties: {
              name: { type: Type.STRING },
              category: { type: Type.STRING },
              purpose: { type: Type.STRING }
            }
          },
          layout: {
            type: Type.OBJECT,
            properties: {
              containerType: { type: Type.STRING },
              columns: { type: Type.NUMBER },
              columnRatios: { type: Type.ARRAY, items: { type: Type.NUMBER } },
              verticalAlignment: { type: Type.STRING },
              gap: { type: Type.STRING }
            }
          },
          style_controls: {
            type: Type.OBJECT,
            properties: {
              colors: { type: Type.OBJECT },
              spacing: { type: Type.OBJECT },
              typography: { type: Type.OBJECT }
            }
          },
          elements: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                id: { type: Type.STRING },
                type: { type: Type.STRING },
                columnIndex: { type: Type.NUMBER },
                content: { type: Type.OBJECT },
                style: { type: Type.OBJECT },
                settings: { type: Type.OBJECT }
              }
            }
          }
        },
        required: ["metadata", "layout", "elements"]
      }
    }
  });
  const data = parseGeminiJson(response.text);
  return {
    ...data,
    id: Math.random().toString(36).substr(2, 9),
    isVisible: true,
    styles: {
      backgroundColor: data.style_controls?.colors?.background || '#ffffff',
      textColor: data.style_controls?.colors?.text || '#000000',
      padding: data.style_controls?.padding || 'py-20'
    },
    title: data.metadata?.name || type,
    content: {}
  };
};

/**
 * Refines text content with AI help
 */
export const refineWebsiteText = async (currentText: string, instruction: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Copywriter. Pas de volgende tekst aan.
    
    HUIDIGE TEKST: "${currentText}"
    INSTRUCTIE: "${instruction}"

    Output ALLEEN de verbeterde tekst, in het Nederlands, geformatteerd als geldige HTML (gebruik <p>, <br>, <ul>, <li>, <strong> waar relevant). Geen markdown, Markdown ticks of uitleg.`
  });
  
  // Verwijder markdown code block syntax (zoals ```html) mocht het model dit alsnog toevoegen
  let output = response.text.trim();
  if (output.startsWith('```html')) output = output.replace(/^```html\n?/, '');
  if (output.endsWith('```')) output = output.replace(/\n?```$/, '');
  
  return output.trim();
};

/** Platte tekst voor klantportaal (geen HTML/markdown). */
export type VoorgesteldProposalAiField = 'recruiterSummary' | 'matchNotes';

export const generateVoorgesteldProposalField = async (
  field: VoorgesteldProposalAiField,
  context: {
    candidateName: string;
    clientName?: string;
    vacancyTitle: string;
    vacancySnippet?: string;
    candidateCvSnippet?: string;
    matchScore?: number;
    existingText?: string;
  }
): Promise<string> => {
  const vacancySnippet = (context.vacancySnippet || '').trim().slice(0, 8000);
  const candidateCvSnippet = (context.candidateCvSnippet || '').trim().slice(0, 8000);
  const existingText = (context.existingText || '').trim().slice(0, 4000);
  const scoreLine =
    typeof context.matchScore === 'number' && context.matchScore > 0
      ? `Interne matchscore (indicatief): ${context.matchScore}%.`
      : '';

  const fieldBrief =
    field === 'recruiterSummary'
      ? `Je schrijft alleen het veld «Samenvatting voor de klant»: een korte, professionele pitch (ongeveer 4–8 zinnen) waarom deze kandidaat de moeite waard is voor ${context.clientName || 'de opdrachtgever'}. Toon: zakelijk-wervend, concreet, geen loze superlatieven.`
      : `Je schrijft alleen het veld «Match CV ↔ vacature»: een duidelijke toelichting voor de klant (2–4 korte alinea's, of genummerde punten per thema) waarin je concrete aansluiting legt tussen ervaring/skills uit het CV en de functie-eisen uit de vacature. Noem ook eventuele aandachtspunten eerlijk maar constructief.`;

  const contents = `${fieldBrief}

CONTEXT (gebruik uitsluitend onderstaande feiten; verzin geen diploma's, werkgevers of jaren die er niet staan):
- Kandidaat: ${context.candidateName}
- Opdrachtgever: ${context.clientName || '—'}
- Vacaturetitel: ${context.vacancyTitle}
${scoreLine}

VACATURETEKST (fragment):
${vacancySnippet || '(niet beschikbaar — wees voorzichtig met details over de functie.)'}

CV / KANDIDAATPROFIEL (fragment):
${candidateCvSnippet || '(niet beschikbaar — beperk je tot algemene formuleringen op basis van de naam en vacature.)'}

${existingText ? `HUIDIGE TEKST VAN DE RECRUITER (hergebruik bruikbare zinnen, verbeter waar nodig):\n${existingText}\n` : ''}

${CLEAN_TEXT_INSTRUCTION}
Lever één JSON-object met alleen het veld "content": de uiteindelijke tekst in het Nederlands, platte tekst. Geen HTML, geen markdown-codeblokken.`;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents,
    config: {
      responseMimeType: 'application/json',
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          content: { type: Type.STRING },
        },
        required: ['content'],
      },
    },
  });
  const parsed = parseGeminiJson(response.text) as { content?: string };
  const out = String(parsed?.content ?? '').trim();
  if (!out) throw new Error('Lege tekst van AI — probeer opnieuw.');
  return out;
};

/**
 * Updates CV Template Design based on user chat
 */
export const updateCVTemplateWithAI = async (currentDesign: any, userPrompt: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een expert CV Designer. De gebruiker wil het ontwerp van zijn CV aanpassen.
    
    HUIDIGE DESIGN CONFIG (JSON):
    ${JSON.stringify(currentDesign)}

    VERZOEK: "${userPrompt}"

    Jouw taak:
    1. Analyseer wat de gebruiker wil (kleuren, fonts, spacing, stijl).
    2. Geef een JSON object terug met *alleen* de gewijzigde velden voor de 'CVDesignConfig'.
    3. Ondersteunde velden: fontFamily, primaryColor, secondaryColor, textColor, backgroundColor, pagePadding (number), itemSpacing (number), fontSizeBody (number), fontSizeHeading (number), headerStyle ('standard'|'sidebar'|'banner').

    Output STRICT JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          fontFamily: { type: Type.STRING },
          primaryColor: { type: Type.STRING },
          secondaryColor: { type: Type.STRING },
          textColor: { type: Type.STRING },
          backgroundColor: { type: Type.STRING },
          pagePadding: { type: Type.NUMBER },
          itemSpacing: { type: Type.NUMBER },
          fontSizeBody: { type: Type.NUMBER },
          fontSizeHeading: { type: Type.NUMBER },
          headerStyle: { type: Type.STRING, enum: ['standard', 'sidebar', 'banner'] }
        }
      }
    }
  });
  return parseGeminiJson(response.text);
};

/**
 * Generates a full career site page as a Craft.js serialized node tree
 */
export const generatePageContent = async (title: string, userPrompt: string, companyName: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Expert Web Designer die pagina's maakt voor Craft.js.
    PAGINA TITEL: "${title}"
    BEDRIJF: "${companyName}"
    GEBRUIKERSWENS: "${userPrompt}"

    Beschikbare blokken (Components):
    - "HeroBlock": properties: "title" (string), "subtitle" (string), "colors": {"background": "#hex", "text": "#hex"}
    - "TextBlock": properties: "title" (string), "text" (string), "alignment" (left, center, right), "colors": {"background": "#hex", "text": "#hex"}
    - "FeaturesBlock": properties: "title" (string), "subtitle" (string), "items" (array of {title, description}), "colors": {"background": "#hex", "text": "#hex"}
    - "VacanciesBlock": properties: "title" (string), "subtitle" (string), "colors": {"background": "#hex", "text": "#hex"}
    - "Container": A simple vertical container padding.

    Maak een layout van 3 tot 5 blokken op basis van de opdracht. ZORG KLAAR VOOR EEN WERVEND RESULTAAT.
    
    Output STRICT JSON ARRAY met de opbouw van de pagina:
    [
      {
        "type": "HeroBlock",
        "props": { "title": "Werken bij ons", "subtitle": "Bouw mee", "colors": { "background": "#ffffff", "text": "#000000" } }
      }
    ]`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            type: { type: Type.STRING },
            props: { type: Type.OBJECT }
          },
          required: ["type", "props"]
        }
      }
    }
  });

  const parsedBlocks = parseGeminiJson(response.text);

  // Convert array of blocks to Craft.js state JSON
  const craftState: any = {
    "ROOT": {
      "type": "Container",
      "isCanvas": true,
      "props": { "colors": { "background": "#F8FAFC" } },
      "displayName": "Container",
      "custom": {},
      "hidden": false,
      "nodes": [],
      "linkedNodes": {}
    }
  };

  if (Array.isArray(parsedBlocks)) {
    parsedBlocks.forEach((block: any, index: number) => {
      const nodeId = `node_${Math.random().toString(36).substr(2, 9)}_${index}`;
      craftState.ROOT.nodes.push(nodeId);
      craftState[nodeId] = {
        "type": block.type,
        "isCanvas": false,
        "props": block.props || {},
        "displayName": block.type,
        "custom": {},
        "hidden": false,
        "nodes": [],
        "linkedNodes": {}
      };
    });
  }

  // Return as stringified Craft JSON state
  return JSON.stringify(craftState);
};

/**
 * Generates a specific suggestion for a CV section based on a vacancy
 */
export const generateVacancySuggestion = async (currentText: string, vacancy: any, sectionType: 'summary' | 'work' | 'education' | 'skills' | 'general' | 'motivation', language: string = 'nl', fieldName?: string) => {
  const vacancyContext = `
    Vacature Titel: ${vacancy.title}
      Beschrijving: ${vacancy.description}
      Keywords: ${vacancy.data?.booleanString || vacancy.data?.keywords || 'N/A'}
      `;

  let specificInstruction = "";
  if (sectionType === 'summary') {
    specificInstruction = `
      Schrijf een korte, professionele CV samenvatting(max 4 zinnen).
      Benadruk de match met de vacature.
      Gebruik actieve taal.
        Taal: ${language === 'en' ? 'Engels' : 'Nederlands'}.
      `;
  } else if (sectionType === 'work') {
    specificInstruction = `
      Herschrijf deze werkervaring.
      Focus op resultaten en taken relevant voor de vacature.
      Maak het krachtiger.
        Taal: ${language === 'en' ? 'Engels' : 'Nederlands'}.
      `;
  } else if (sectionType === 'skills') {
    specificInstruction = `
      Geef een lijst van 5 relevante skills die in de vacature worden gevraagd en waarschijnlijk bij deze kandidaat passen.
      Geef alleen de skills, komma gescheiden.
        Taal: ${language === 'en' ? 'Engels' : 'Nederlands'}.
      `;
  } else if (sectionType === 'motivation') {
    specificInstruction = `
      Schrijf 2 alinea's voor in een motivatiebrief. Geen lange zinnen en begin soms een zin met "ik", maar nooit de eerste zin van de alinea. 
      Beschrijf in de eerste alinea waarom hij/zij de rol zo graag wil (de vacature heb ik hiervoor gestuurd) en zijn/haar eerdere ervaring (benoem de overheden en relevante functies) en in de tweede alinea (beginnend met "Wat mij tekent als persoon…") wat meer van zijn/haar karaktereigenschappen. 
      Gebruik voor de motivatie de huidige tekst ("HUIDIGE TEKST VAN KANDIDAAT"), deze is leidend. Vul waar nodig aan met het cv. 
      Noem eerst zijn/haar aanleiding/affiniteit met de opdracht en noem de rol en bedrijf pas in de 3e zin. 
      Voeg geen headers boven de alinea's.
      Taal: ${language === 'en' ? 'Engels' : 'Nederlands'}.
    `;
  } else {
    if (fieldName) {
      specificInstruction = `
        Genereer of optimaliseer de content voor het CV-onderdeel: "${fieldName}". 
        Zorg dat het perfect aansluit bij de eisen van de vacature. Als de huidige tekst leeg is, schrijf dan een nieuwe passende tekst onder deze titel.
        Taal: ${language === 'en' ? 'Engels' : 'Nederlands'}.
      `;
    } else {
      specificInstruction = `
        Optimaliseer deze tekst voor de vacature.
          Taal: ${language === 'en' ? 'Engels' : 'Nederlands'}.
        `;
    }
  }
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Expert CV Coach.

        VACATURE:
    ${vacancyContext.substring(0, 5000)}

    HUIDIGE TEKST VAN KANDIDAAT(${fieldName || sectionType}):
      "${currentText}"

      OPDRACHT:
    ${specificInstruction}

    Output alleen de verbeterde tekst.Geen 'Hier is de tekst' of quotes.
        Voor 'skills', geef alleen de lijst.`
  });

  return response.text.trim();
};

export const searchTalentPool = async (query: string, candidates: any[], customPrompt?: string) => {
  // Create a minified list of candidates for the prompt to save tokens
  const candidateSummaries = candidates?.map(c => ({
    id: c.candidate?.id || c.id,
    name: c.candidate?.name || c.name || 'Onbekend',
    current_role: c.parsed_cv?.currentRole || c.ai_parsed_data?.currentRole || c.parsed_cv?.role || c.ai_parsed_data?.role || c.ai_parsed_data?.["Function/Role title"] || c.candidate?.role || c.role || 'Onbekend',
    skills: (c.parsed_cv?.tags || c.ai_parsed_data?.tags || c.ai_parsed_data?.["Key Skills"] || c.candidate?.skill_set || c.skill_set || []).join(', '),
    recent_experience: (c.parsed_cv?.timeline || c.ai_parsed_data?.timeline || [])
      .slice(0, 3) // Only last 3 jobs
      .map((t: any, idx: number) => `${idx === 0 ? '[MEEST RECENTE BAAN]: ' : ''}${t.role || t.title} bij ${t.company} (${t.year || t.period})`).join('; '),
    summary: String(c.parsed_cv?.summary || c.ai_parsed_data?.summary || c.ai_parsed_data?.Summary || c.candidate?.cv_summary || c.cv_summary || '').substring(0, 200) // Truncate summary
  })) || [];

  const instructionContent = customPrompt ? `\nMAATWERK INSTRUCTIES VAN DE KLANT:\n"${customPrompt}"\n\nPas bovenstaande instructies STRICT toe in je analyse.\n` : ``;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Expert Recruiter. De onderstaande lijst kandidaten is al strak pre-geselecteerd door een wiskundige Vector-Database als de top-matches op basis van taalafstand. Jouw taak is om deze ijzersterke voorselectie kritisch te overhoren, exact te scoren, en uit te leggen WAAROM ze matchen op deze specifieke zoekopdracht.

      ZOEKOPDRACHT: "${query}"
      ${instructionContent}

      MATCHING REGELS:
      1. HUIDIGE ROL IS HEILIG: Geef prioriteit aan kandidaten waarvan de 'current_role' of '[MEEST RECENTE BAAN]' exact overeenkomt of sterk gerelateerd is (Hoogste weging).
      2. NIVEAU BEWAKING (Senioriteit): Voorkom overkwalificatie. Als er om een Junior/Admin wordt gevraagd, bestraf dan Directors/Managers hard met een lage score. Als er om Manager wordt gevraagd, bestraf dan starters. Let scherp op functieniveau in de zoekopdracht.
      3. VOLLEDIGE RECALL: Geef GEEN harde limiet. Evalueer ALLES en geef álle kandidaten terug die een score van 60 of hoger behalen.
      4. COMPENSATIE VOOR KORTE PROFIELEN: Bestraf lege tekstvelden NIET als een kandidaat wél de juiste tags ('skills') of 'current_role' (de vacaturesite upload extraheert vaak in "Summary" en "Key Skills") in hun samenvatting heeft staan. Weeg dit mee in plaats van ze uit te sluiten.

    KANDIDATEN:
    ${JSON.stringify(candidateSummaries)}

    Geef een JSON lijst terug van ALLE relevante matches (score >= 60). Sorteer ze NIET, dat doen we lokaal. Geef voor elke match een gedetailleerde score out of 100.
      Format:
    {
      "matches": [
        { 
          "id": "candidate_id", 
          "reason": "Korte uitleg waarom deze kandidaat matcht (max 1 zin)",
          "score": 85,
          "breakdown": {
            "title": 90,
            "seniority": 80,
            "skills": 85,
            "experience": 85
          }
        }
      ]
    } `,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          matches: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                id: { type: Type.STRING },
                reason: { type: Type.STRING },
                score: { type: Type.NUMBER },
                breakdown: {
                  type: Type.OBJECT,
                  properties: {
                    title: { type: Type.NUMBER },
                    seniority: { type: Type.NUMBER },
                    skills: { type: Type.NUMBER },
                    experience: { type: Type.NUMBER }
                  }
                }
              }
            }
          }
        }
      }
    }
  });

  return parseGeminiJson(response.text);
};

/**
 * Generates email or SMS content based on a prompt
 */
export const generateTemplateContent = async (prompt: string, type: 'email' | 'sms') => {
  const context = type === 'email'
    ? "Je schrijft een professionele, wervende zakelijke e-mail."
    : "Je schrijft een kort, bondig en professioneel SMS bericht (max 160 tekens).";

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `
    ROL: ${FROUKJE_PROMPT}
    CONTEXT: ${context}

    OPDRACHT: Schrijf content voor een ${type} template gebaseerd op: "${prompt}".
      
      BELANGRIJK VOOR EMAIL:
    - Gebruik placeholders waar logisch: { { candidate_name } }, { { company_name } }, { { vacancy_title } }.
    - Gebruik HTML formatting(<p>, <br>, <strong>) indien nodig voor structuur, maar geen markdown blocks.
      - Schrijf een pakkende Subject line indien het een email is.
      
      BELANGRIJK VOOR SMS:
    - Geen HTML.
      - Geen Subject.
      - Max 160 tekens.

      OUTPUT JSON:
    {
      "subject": "Onderwerp (alleen voor email, anders leeg)",
        "content": "De inhoud van het bericht"
    }
    `,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          subject: { type: Type.STRING },
          content: { type: Type.STRING }
        },
        required: ["content"]
      }
    }
  });

  return parseGeminiJson(response.text);
};

/**
 * Generates a complete AutomationFlow sequence from a conversational chat prompt.
 */
export const generateAutomationFlowChat = async (prompt: string, history: any[] = []) => {
  // Format history for Gemini
  const formattedHistory = history.map(msg => ({
    role: msg.role === 'model' ? 'model' : 'user',
    parts: [{ text: msg.content }]
  }));

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: [
      {
        role: 'user',
        parts: [{
          text: `Je bent Froukje, een uiterst behulpzame Expert Automation Architect.
          Jouw taak is om samen met de gebruiker een recruitment automatisering flow te bouwen. Vraag door als er dingen onduidelijk zijn.
          Als de gebruiker een flow beschrijft die compleet genoeg is (of na jouw verfijning), genereer dan de flow JSON in de 'flow' property. Anders laat je die leeg.
      
          BESCHIKBARE TRIGGERS: 'application_created', 'status_rejected', 'status_interview', 'candidate_birthday', 'vacancy_published', 'sla_overdue', 'candidate_hired', 'manual_trigger'
          BESCHIKBARE ACTIONS: 'email', 'sms', 'whatsapp', 'create_task', 'internal_note', 'stage_change', 'notification', 'docusign', 'ai_score', 'wait', 'condition', 'add_tag'
      
          INSTRUCTIES VOOR FLOW JSON (wanneer je deze bouwt):
          1. Geef de flow een 'name' en 'description'. HOUD DEZE KORT EN BONDIG (Max 1 zin).
          2. Selecteer de juiste trigger.
          3. Voor email/sms/whatsapp: 
             - Vul altijd 'config.customMessage' in met een professionele tekst (max 3-4 zinnen), inclusief tags zoals {{candidate.first_name}}.
             - Vul 'config.from' in als er een specifieke afzender gewenst is (bijv. naam van recruiter of bedrijf).
             - Vul 'config.to' in met de juiste tag (bijv. {{candidate.email}} of {{candidate.phone}} of een specifiek adres).
             - Voor email, vul ook 'config.subject' in.
          4. Voor create_task: vul 'config.taskTitle' in (Max 5 woorden) en evt 'config.taskPriority' ('Low'|'Medium'|'High').
          5. Voor wait: vul 'config.delay' en 'config.delayUnit' ('minutes'|'hours'|'days') in.
          6. Voor add_tag: vul 'config.tagName' in.
          7. Voor stage_change: vul 'config.targetStage' in.
          8. Voor internal_note: vul 'config.noteContent' in.
          9. ALS de gebruiker een "nieuwe" of complexe trigger vraagt (bijv. "Als score hoog is", "Als type X is"): kies een beschikbare trigger zoals 'application_created' of 'stage_change' EN VOEG DAARNA GELIJK EEN 'condition' STAP TOE. In deze 'condition' stap vul je in: 'config.conditions' = [{field: 'candidate.score'|'candidate.stage', operator: 'equals'|'greater_than'|'contains', value: '80'|'Interview'}], en 'config.logicAction' = 'stop' om de stroom af te breken als het NIET klopt.
          10. CRUCIAAL: Vul in de 'config' ALLEEN de velden in die benodigd zijn voor het gekozen 'type'. Laat alle andere velden geheel weg (bijv. genereer GEEN 'taskTitle' in een 'email' stap). Voorkom lange herhalende teksten.
          
          OUTPUT VERPLICHT JSON MET DEZE SCHEMA (chatbericht in 'message', flow optioneel):`
        }]
      },
      ...formattedHistory,
      {
        role: 'user',
        parts: [{ text: prompt }]
      }
    ],
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          message: { type: Type.STRING }, // Your conversational response to the user
          flow: {
            type: Type.OBJECT, // Only populate this if you have enough info to build the flow
            nullable: true,
            properties: {
              name: { type: Type.STRING },
              description: { type: Type.STRING },
              trigger: { type: Type.STRING },
              steps: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    type: { type: Type.STRING },
                    label: { type: Type.STRING },
                    config: {
                      type: Type.OBJECT,
                      properties: {
                        templateId: { type: Type.STRING },
                        from: { type: Type.STRING },
                        to: { type: Type.STRING },
                        subject: { type: Type.STRING },
                        customMessage: { type: Type.STRING },
                        delay: { type: Type.NUMBER },
                        delayUnit: { type: Type.STRING, enum: ['minutes', 'hours', 'days'] },
                        taskTitle: { type: Type.STRING },
                        taskPriority: { type: Type.STRING, enum: ['Low', 'Medium', 'High'] },
                        tagName: { type: Type.STRING },
                        targetStage: { type: Type.STRING },
                        noteContent: { type: Type.STRING },
                        conditions: {
                          type: Type.ARRAY,
                          items: {
                            type: Type.OBJECT,
                            properties: {
                              field: { type: Type.STRING },
                              operator: { type: Type.STRING, enum: ['equals', 'contains', 'greater_than', 'less_than', 'is_set', 'is_not_set'] },
                              value: { type: Type.STRING }
                            }
                          }
                        },
                        logicAction: { type: Type.STRING, enum: ['continue', 'stop'] }
                      }
                    }
                  },
                  required: ["type", "label", "config"]
                }
              }
            },
            required: ["name", "description", "trigger", "steps"]
          }
        },
        required: ["message"]
      }
    }
  });

  return parseGeminiJson(response.text);
};

/**
 * Standardizes a massive, raw Bullhorn JSON response into Werftools clean `ai_parsed_data` format
 */
export const parseBullhornRawToProfile = async (rawCandidate: any) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Expert Recruiter en Data Analist. 
    Hier is de ruwe JSON data van een kandidaat, geëxporteerd uit Bullhorn ATS. 
    Jouw taak is dit om te zetten naar een extreem strak, gestandaardiseerd JSON profiel dat direct in ons Werftools portaal kan.
    
    BULLHORN RUWE DATA:
    ${JSON.stringify(rawCandidate).substring(0, 15000)}
    
    Instructies:
    1. Gebruik alle beschikbare tekstvelden (description, customText, experience, etc.) om een representatief overzicht te schetsen.
    2. 'role' en 'currentRole' moeten hun laatste/huidige functietitel weergeven.
    3. Vul 'skills' en 'tags' aan met komma gescheiden keywords die je écht uithaalt uit hun tekst/profiel.
    4. Extract in 'summary' alléén de introductie of het 'persoonlijk profiel' van de kandidaat. Neem dit stuk tekst LETTERLIJK (verbatim) over zonder zinnen te herschrijven of te veranderen! De taal, woorden en originaliteit moeten behouden blijven. Let op: filter wél alle gestructureerde rest-delen uit deze field zoals opgesomde werkervaringen, opleidingen en platte lijsten met hobby's.
    5. Bouw een gestructureerde 'timeline' of 'workHistories' én 'educations' lijst aan de hand van de array elementen EN de rest-tekst uit de description. Belangrijk: Neem hierin ALLE werkervaringen en opleidingen exact over, laat er GEEN over. Neem ook de beschrijving (description) van de werkervaring en opleiding VERBATIM (LETTERLIJK) en COMPLEET over. NIET samenvatten, NIET herschrijven en NIETS weglaten uit de originele tekst! Voeg absoluut GEEN ongevraagde opsommingstekens (bullets) toe.
    6. Als je geen mail of telefoon kan fixen, zet die op "Unknown".
    7. 'availability' kan null zijn als niet vermeld.
    8. CRUCIAAL: VERZIN NOOIT GEGEVENS! Als bepaalde werkervaring, opleidingen of andere data NIET in de aangeleverde brontekst of ruwe JSON te vinden is, vul deze dan NIET in. Gebruik NOOIT externe kennis of aannames om ontbrekende delen aan te vullen.
    
    Output STRICT JSON in the defined schema without missing required properties.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          firstName: { type: Type.STRING },
          lastName: { type: Type.STRING },
          email: { type: Type.STRING },
          phone: { type: Type.STRING },
          currentRole: { type: Type.STRING },
          role: { type: Type.STRING },
          summary: { type: Type.STRING },
          skills: { type: Type.ARRAY, items: { type: Type.STRING } },
          tags: { type: Type.ARRAY, items: { type: Type.STRING } },
          timeline: {
             type: Type.ARRAY,
             items: {
               type: Type.OBJECT,
               properties: {
                 title: { type: Type.STRING },
                 company: { type: Type.STRING },
                 period: { type: Type.STRING },
                 description: { type: Type.STRING }
               }
             }
          },
          educations: {
             type: Type.ARRAY,
             items: {
               type: Type.OBJECT,
               properties: {
                 school: { type: Type.STRING },
                 degree: { type: Type.STRING },
                 period: { type: Type.STRING }
               }
             }
          },
          availability: { type: Type.STRING, nullable: true },
          languages: { type: Type.ARRAY, items: { type: Type.STRING }, nullable: true }
        },
        required: ["firstName", "lastName", "summary", "skills", "timeline"]
      }
    }
  });

  return parseGeminiJson(response.text);
};

export const parseBullhornRawToVacancy = async (rawBhData: any) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: [{ role: 'user', parts: [{ text: JSON.stringify(rawBhData, null, 2) }] }],
    systemInstruction: `Je bent een gespecialiseerde recruitment data parser voor Werftools.
    De gebruiker stuurt een ruwe, vaak chaotische JSON uitsnede van een Bullhorn 'JobOrder' (een vacature).
    
    Jouw taak is om deze gegevens om te zetten in een super strak AI profiel en de CV/vacaturetekst in leesbare secties op te delen.
    
    Instructies:
    1. 'title': Formuleer een krachtige, korte, wervende functietitel (haal rare interne codes of streepjes weg).
    2. 'category': Bepaal de hoofdcategorie aan de hand van de titel en beschrijving (bijv. 'IT', 'Finance', 'Zorg', 'Sales').
    3. 'summary': Schrijf een uiterst soepele, wervende en wervelende introductie/samenvatting (max 3 zinnen) op basis van de description. Focus op de 'waarom' (wat maakt het leuk).
    4. 'keywords': Extract minimaal 5 en maximaal 10 'hard statements' of 'skills' uit de description (technologieën, eisen). Geef dit terug als array van strings.
    5. 'builtVacancySections': Ontleed en herschrijf de volledige ruwe vacaturetekst/description naar gestructureerde, schone secties (bijv: "De Functie", "Functie-eisen", "Ons Aanbod"). Retourneer the content in heldere, zakelijke tekst.
    
    Output STRICT JSON.`,
    config: {
      responseMimeType: "application/json",
      maxOutputTokens: 2048,
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          category: { type: Type.STRING },
          summary: { type: Type.STRING },
          keywords: { type: Type.ARRAY, items: { type: Type.STRING } },
          builtVacancySections: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                title: { type: Type.STRING },
                content: { type: Type.STRING }
              },
              required: ["title", "content"]
            }
          }
        },
        required: ["title", "summary", "keywords", "category", "builtVacancySections"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

/**
 * Performs a real semantic match between a candidate profile and vacancy requirements
 */
export const performRealMatch = async (candidateProfile: any, vacancy: any, settings?: any) => {
  const candidateContext = `
    Kandidaat: ${candidateProfile.name}
    Huidige Rol: ${candidateProfile.role}
    Samenvatting: ${candidateProfile.summary || 'N/A'}
    Skills: ${candidateProfile.skills ? candidateProfile.skills.join(', ') : 'N/A'}
    Ervaring: ${JSON.stringify(candidateProfile.timeline || [])}
    `;

  const vacancyContext = `
    Vacature Titel: ${vacancy.title}
    Beschrijving: ${vacancy.description}
    Eisen / Skills: ${vacancy.data?.booleanString || vacancy.data?.keywords || 'N/A'}
    `;

  // Custom Prompt Injection
  let systemInstruction = `Je bent een expert recruiter. Vergelijk de kandidaat met de vacature.
  SCHRIJF ALTIJD IN HET VOLLEDIG NEDERLANDS. Geen Engelse zinnen in de output.

      ${candidateContext}
    
    ${vacancyContext}

    Geef een harde, eerlijke analyse in het Nederlands.
    1. Score(0 - 100) gebaseerd op hard skills en ervaring.
    2. Korte reden in het Nederlands (max 2 zinnen). Geen algemene uitspraken.
    3. Pluspunten in het Nederlands (max 3). Alleen concrete punten uit het CV.
    4. Aandachtspunten in het Nederlands (max 3). Alleen wat écht ontbreekt.

    Output JSON.`;

  const customPromptFromSettings = settings?.ai_prompts_settings?.talentpoolMatch || settings?.customPrompt;
  if (customPromptFromSettings) {
    systemInstruction = `${customPromptFromSettings}

    CONTEXT:
    ${candidateContext}

    VACATURE:
    ${vacancyContext}
    
    Output JSON zoals gevraagd: { score, reason, pros, cons } `;
  }

  // Weight Injection
  if (settings?.weights) {
    systemInstruction += `\n\nBELANGRIJK - GEBRUIK DEZE WEEGSCHALEN VOOR DE SCORE:
    - Hard Skills: ${settings.weights.hardSkills}%
      - Soft Skills: ${settings.weights.softSkills}%
        - Ervaring: ${settings.weights.experience}%
          - Culture Fit: ${settings.weights.culturalFit}%

            Baseer de eindscore(0 - 100) strikt op deze verdeling.`;
  }

  const response = await ai.models.generateContent({
    model: settings?.model || AI_PROXY_CHAT_MODEL,
    contents: systemInstruction,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          score: { type: Type.NUMBER },
          reason: { type: Type.STRING },
          pros: { type: Type.ARRAY, items: { type: Type.STRING } },
          cons: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: ["score", "reason", "pros", "cons"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

export const performBatchMatch = async (candidateProfile: any, activeVacancies: any[], settings?: any) => {
  const candidateContext = `
    Kandidaat: ${candidateProfile.name}
    Huidige Rol: ${candidateProfile.role}
    Samenvatting: ${candidateProfile.summary || 'N/A'}
    Skills: ${candidateProfile.skills ? candidateProfile.skills.join(', ') : 'N/A'}
    Ervaring: ${JSON.stringify(candidateProfile.timeline || [])}
    `;

  const vacanciesContext = activeVacancies.map(v => `
    [VACATURE ID: ${v.id}]
    Vacature Titel: ${v.title}
    Beschrijving: ${v.description}
    Eisen / Skills: ${v.data?.booleanString || v.data?.keywords || 'N/A'}
    `).join('\n\n');

  let systemInstruction = `Je bent een expert recruiter. Vergelijk de kandidaat met AL deze openstaande vacatures.
  SCHRIJF ALTIJD IN HET VOLLEDIG NEDERLANDS. Geen Engelse zinnen in de output.
  
      ${candidateContext}

    Openstaande Vacatures:
    ${vacanciesContext}

    Geef voor ELKE vacature een harde, eerlijke analyse in het Nederlands.
    1. Score(0 - 100) gebaseerd op hard skills en ervaring.
    2. Korte reden in het Nederlands (max 2 zinnen). Geen algemene uitspraken.
    3. Pluspunten in het Nederlands (exact 5). Alleen concrete punten uit het CV.
    4. Aandachtspunten in het Nederlands (exact 5). Alleen wat écht ontbreekt in dit CV of wat beter kan.
    
    Output formaat is een JSON Array van waarden. LET OP: Gebruik exact de opgegeven "vacancyId" in je output.`;

  if (settings?.weights) {
    systemInstruction += `\n\nBELANGRIJK - GEBRUIK DEZE WEEGSCHALEN VOOR DE SCORE:
    - Hard Skills: ${settings.weights.hardSkills}%
      - Soft Skills: ${settings.weights.softSkills}%
        - Ervaring: ${settings.weights.experience}%
          - Culture Fit: ${settings.weights.culturalFit}%
          
            Baseer de eindscore (0-100) strikt op deze verdeling.`;
  }

  const response = await ai.models.generateContent({
    model: settings?.model || AI_PROXY_CHAT_MODEL,
    contents: systemInstruction,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: {
            type: Type.OBJECT,
            properties: {
              vacancyId: { type: Type.STRING },
              score: { type: Type.NUMBER },
              reason: { type: Type.STRING },
              pros: { type: Type.ARRAY, items: { type: Type.STRING } },
              cons: { type: Type.ARRAY, items: { type: Type.STRING } }
            },
            required: ["vacancyId", "score", "reason", "pros", "cons"]
        }
      }
    }
  });
  return parseGeminiJson(response.text);
};

export const generateTailoredCV = async (candidateProfile: any, vacancy: any, aiName: string = 'Froukje', customInstruction?: string) => {
  const candidateContext = `
    Kandidaat: ${candidateProfile.name}
    Huidige Rol: ${candidateProfile.role}
    Samenvatting: ${candidateProfile.summary || 'N/A'}
    Skills: ${candidateProfile.skills ? candidateProfile.skills.join(', ') : 'N/A'}
    Ervaring: ${JSON.stringify(candidateProfile.timeline || [])}
    `;

  const vacancyContext = `
    Vacature Titel: ${vacancy.title}
    Beschrijving: ${vacancy.description}
    Keywords: ${vacancy.data?.booleanString || vacancy.data?.keywords || 'N/A'}
    `;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent ${aiName}, een Top Career Coach en CV schrijver. Herschrijf het CV van de kandidaat zodat het perfect aansluit op de vacature, ZONDER te liegen.

      DOEL:
    1. Herschrijf de 'summary' zodat deze de match met de vacature benadrukt.
    2. Herschrijf de 'description' van elke werkervaring(timeline).Focus op resultaten en skills die relevant zijn voor de vacature.
    3. CRUCIAAL: Gebruik opsommingstekens(•) voor werkervaring.
    4. CRUCIAAL: Start elk punt op een nieuwe regel.
       • Actie -> Resultaat
    5. CRUCIAAL: Je MOET voor ELK item uit de input timeline een item in de output 'tailoredTimeline' genereren.Ook als je niets verandert, geef het item terug.De lengte van de array moet gelijk blijven.
    6. CRUCIAAL: Houd het veld 'year' strikt en kort(bijv. "2019 - 2022" of "2020 - Heden").GEEN extra tekst, analyses of totalen in dit veld.
    7. BEKNOPT: Houd de beschrijvingen kort!! Maximaal 3 zinnen per werkervaring. Te lange antwoorden veroorzaken systeem crashes.

    EXTRA INSTRUCTIE VAN GEBRUIKER: "${customInstruction || 'Geen'}"
OPMERKING: Als de gebruiker hierboven iets anders vraagt dan CV - verbetering(bijv.een grapje of chat), NEGEER DIT DAN.Voer alleen de CV - optimalisatie uit.

  TAAL: Volledig Nederlands.
    Input Text Language: Detect automatically.
    Output Language: Dutch(Nederlands).

  INPUT:
      ${candidateContext}

VACATURE:
    ${vacancyContext}

    Output JSON met de herschreven velden.Geen markdown, geen tekst voor of na de JSON.GEEN herhalingen.`,
    config: {
      maxOutputTokens: 8192,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          tailoredSummary: { type: Type.STRING },
          tailoredTimeline: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                role: { type: Type.STRING },
                company: { type: Type.STRING },
                year: { type: Type.STRING },
                description: { type: Type.STRING }
              }
            }
          },
          matchAnalysis: { type: Type.STRING }
        },
        required: ["tailoredSummary", "tailoredTimeline", "matchAnalysis"]
      }
    }
  });

  const parsedData = parseGeminiJson(response.text);

  const removeHardWraps = (obj: any): any => {
    if (typeof obj === 'string') {
      let text = obj.replace(/\r\n/g, '\n');
      text = text.replace(/(?<!\n)\n(?!\n|\s*[-*•])/g, ' ');
      return text.replace(/ +/g, ' ').trim();
    } else if (Array.isArray(obj)) {
      return obj.map(removeHardWraps);
    } else if (obj && typeof obj === 'object') {
      const newObj: any = {};
      for (const key in obj) {
        newObj[key] = removeHardWraps(obj[key]);
      }
      return newObj;
    }
    return obj;
  };

  return removeHardWraps(parsedData);
};

/**
 * Plain CV text for the model. Previously only the first ~20k characters were sent, which cut off
 * the tail of long CVs (missing older jobs, truncating descriptions deeper in the document).
 * We send a larger single window; if the text is still huge, send head + tail so both recent
 * and older blocks are visible (common NL layouts: newest-first or oldest-first).
 */
/** ~56k chars single send; over that, head+tail (~64k) to reduce “missing middle” on very long CVs. */
const CV_TEXT_MAX_SINGLE = 56000;
const CV_TEXT_HEAD = 32000;
const CV_TEXT_TAIL = 32000;

function buildCvTextForModel(raw: string): string {
  const text = (raw || '').replace(/\r\n/g, '\n').trim();
  if (!text) return '';
  if (text.length <= CV_TEXT_MAX_SINGLE) return text;
  const sep =
    '\n\n===== [ZELFDE CV — TWEEDE TEKSTBLOK (EINDE VAN HET DOCUMENT); EXTRAHEER OOK WERKERVARING UIT DIT BLOK] =====\n\n';
  return text.slice(0, CV_TEXT_HEAD) + sep + text.slice(-CV_TEXT_TAIL);
}

function normalizeCvTimeline(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;
  const out: any = normalizeCvAnalysisResult(obj);
  if (!Array.isArray(out.timeline) || out.timeline.length === 0) return out;
  out.timeline = out.timeline.map((t: any) => {
    if (!t || typeof t !== 'object') return t;
    const row: any = { ...t };
    const hasYear = row.year != null && String(row.year).trim().length > 0;

    if (row.startDate && row.endDate) {
      row.year = `${String(row.startDate).trim()} – ${String(row.endDate).trim()}`;
    } else if (!hasYear) {
      const periodLike =
        row.period ||
        row.dates ||
        row.fromTo ||
        row.tijd ||
        row.jaar ||
        row.dateRange ||
        row.startDate ||
        row.endDate;
      if (periodLike != null && String(periodLike).trim()) {
        row.year = String(periodLike).trim();
      }
    }

    if (row.location == null || row.location === '') {
      row.location = row.plaats || row.city || row.locatie || row.region || '';
    }
    return row;
  });
  return out;
}

export const analyzeUploadedCV = async (input: string | { base64: string, mimeType: string }, vacancyContext?: string, bullhornDescription?: string) => {
  const parts: any[] = [];

    // Add the file content if provided as base64, otherwise add text
    if (typeof input === 'string') {
        parts.push({ text: `CV TEKST INHOUD: \n${buildCvTextForModel(input)} ` });
    } else {
        const cleanBase64 = input.base64.replace(/\s+/g, '');
        if (input.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            try {
                const formatRes = await fetch(`${GEMINI_PROXY_BASE}/api/parse-cv/docx-to-text`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ base64: cleanBase64 })
                });
                if (formatRes.ok) {
                    const data = await formatRes.json();
                    parts.push({ text: `CV TEKST INHOUD: \n${buildCvTextForModel(String(data.text || ''))} ` });
                } else {
                    console.warn("Failed to extract docx text locally. Falling back to raw parts.");
                    parts.push({ inlineData: { mimeType: input.mimeType, data: cleanBase64 } });
                }
            } catch (err) {
                console.error("Local docx parsing error:", err);
                parts.push({ inlineData: { mimeType: input.mimeType, data: cleanBase64 } });
            }
        } else if (input.mimeType === 'application/msword') {
             try {
                const formatRes = await fetch(`${GEMINI_PROXY_BASE}/api/parse-cv/doc-to-text`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ base64: cleanBase64 })
                });
                if (formatRes.ok) {
                    const data = await formatRes.json();
                    parts.push({ text: `CV TEKST INHOUD: \n${buildCvTextForModel(String(data.text || ''))} ` });
                } else {
                    console.warn("Failed to extract .doc text locally via CloudConvert. Falling back to warning.");
                    parts.push({ text: "Waarschuwing: Originele CV is een oud .doc formaat (application/msword) en kon niet gelezen of geüpload worden naar Gemini. Als er geen extra Bullhorn ruwe tekst is, kun je waarschijnlijk weinig tot niets extraheren. Adviseer de gebruiker om het document om te slaan naar .pdf of het te proberen via tekst invoer." });
                }
            } catch (err) {
                console.error("Local doc parsing error:", err);
                parts.push({ text: "Waarschuwing: Originele CV is een oud .doc formaat (application/msword) en kon niet gelezen of geüpload worden naar Gemini. Als er geen extra Bullhorn ruwe tekst is, kun je waarschijnlijk weinig tot niets extraheren. Adviseer de gebruiker om het document om te slaan naar .pdf of het te proberen via tekst invoer." });
            }
        } else if (input.mimeType === 'application/pdf') {
            // Azure OpenAI vision input does not accept PDFs as image_url. Extract text on the server instead.
            try {
                const formatRes = await fetch(`${GEMINI_PROXY_BASE}/api/parse-cv/pdf-to-text`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ base64: cleanBase64 }),
                    signal: AbortSignal.timeout(300000),
                });
                const data = await formatRes.json().catch(() => ({}));
                const text = (data?.text || '').toString();
                const trimmed = text.trim();
                if (formatRes.ok && trimmed.length > 0) {
                    parts.push({ text: `CV TEKST INHOUD: \n${buildCvTextForModel(trimmed)} ` });
                } else if (formatRes.ok && trimmed.length === 0) {
                    const scanErr = new Error('PDF_NO_TEXT');
                    (scanErr as Error & { userMessage?: string }).userMessage =
                        String(data?.warning || data?.error || '').trim() ||
                        'Deze PDF bevat geen selecteerbare tekst (scan). Upload een .docx of plak de CV-tekst.';
                    throw scanErr;
                } else {
                    console.warn('Failed to extract pdf text via server.', data);
                    parts.push({
                        text: 'Waarschuwing: Dit CV is een PDF, maar de tekst kon niet worden uitgelezen. Probeer opnieuw, upload een .docx, of plak de CV-tekst handmatig.',
                    });
                }
            } catch (err) {
                if ((err as Error)?.name === 'TimeoutError') {
                    throw new Error('PDF_PARSE_TIMEOUT');
                }
                // Preserve explicit signal for scanned PDFs
                if (
                    (err as any)?.message === 'PDF_NO_TEXT' ||
                    String((err as any)?.message || '').includes('PDF bevat geen selecteerbare tekst')
                ) {
                    throw err;
                }
                console.error("Server pdf parsing error:", err);
                parts.push({ text: "Waarschuwing: Dit CV is een PDF, maar de tekst kon niet worden uitgelezen. Probeer opnieuw, upload een .docx, of plak de CV-tekst handmatig." });
            }
        } else {
            parts.push({
                inlineData: {
                    mimeType: input.mimeType,
                    data: cleanBase64
                }
            });
        }
    }

    if (bullhornDescription && bullhornDescription.trim().length > 0) {
        parts.push({ text: `EXTRA RUWE DATA UIT BULLHORN DATABASE:\n${bullhornDescription.substring(0, 5000)}\n\nIntegreer of valideer hiermee de werkgeschiedenis indien het CV onduidelijk is.` });
    }

    // Add the instruction prompt
  parts.push({
    text: `Analyseer het bovenstaande CV bestand / tekst in de context van deze vacature: "${vacancyContext || 'Algemeen'}".
    
    Jouw taak:
    1. Extraheer persoonsgegevens (Naam/firstName, Achternaam/lastName, Email, Telefoon, LinkedIn, Adres/address, Postcode/zipCode, Woonplaats/city, Land/country, Voorkeurstaal/preferredLanguage, Geboortedatum/dateOfBirth, Geboorteplaats/placeOfBirth).
       - LET OP (NL CV's): persoonsgegevens staan vaak onder kopjes als "Persoonsgegevens", "Contact", "Adres", "Profiel", of in de header.
       - E-mail: zoek patronen met '@' (bv. jan.pieters@gmail.com).
       - Telefoon: zoek NL nummers (bv. +31 6 12345678, 06-12345678).
       - Postcode: NL postcode patroon (bv. 1234 AB).
       - Woonplaats/stad: staat vaak direct na postcode/plaats of bij "Woonplaats".
       Vul bij 'currentRole' (Huidige Rol) ALTIJD de functietitel in van de meest recente (laatste) werkervaring.
    2. Genereer 5 - 7 relevante "Tags"(hard skills / tools).
    3. 'summary' en 'Motivation': Neem de persoonlijke introductie EXACT (verbatim) over vanuit het CV. Zoek in het CV heel actief naar dit onderdeel, dat vaak vooraan staat onder kopjes als: "Over mij", "Over mezelf", "Motivatie", "Profiel", "Persoonlijk Profiel", of "Wie ik ben". Je mag GEEN zinnen veranderen, GEEN tekst toevoegen en niets herschrijven.
    4. Geef een "Match Score"(0 - 100) en een korte reden.
    5. Extraheer de werkervaring (tijdlijn / 'timeline'):
       - Als de CV-tekst twee blokken bevat gescheiden door de marker "[ZELFDE CV — TWEEDE TEKSTBLOK", hoort alles bij dezelfde kandidaat: extraheer werkervaring uit beide blokken en voeg samen (chronologisch, zonder dubbele rollen).
       - Neem maximaal 22 items op (meest recent eerst), maar splits liever te veel dan te weinig: elke aparte opdracht, interim-klus, of projectregel in de bron die eigen periode en/of opdrachtgever heeft → eigen object in 'timeline'.
       - INTERIM / ZZP / EIGEN BEDRIJF: Zet NOOIT alle opdrachten van een interim of ZZPer als één lange 'description' onder alleen het eigen bedrijf, tenzij het CV letterlijk zo is opgebouwd zonder enige splitsing. Bij blokken als "Opdrachten", "Projecten", "Lopende opdrachten", "Deelopdrachten", of datum + titel per regel: maak per opdracht een eigen rij met company = opdrachtgever of projectnaam (of "Zelfstandig" / "Project" volgens eerdere regel als er geen naam is).
       - DATUM / PERIODE (veld 'year', verplicht invullen als de bron enige datum heeft): gebruik exact wat in het CV staat, bijv. "jan 2019 – heden", "Q1 2018 – Q3 2019", "2011 – 2013", "06/2015 – 08/2015", "kwartaal 3 2020". Zet NOOIT leeg 'year' als er bij die werkzaamheid ergens een jaar, kwartaal of maand-jaar in de brontekst staat (ook als die alleen op de eerste regel van het blok staat).
       - KOPPELING: role, company, year, location en description horen bij dezelfde werkzaamheid als in het CV; schuif geen bedrijfsnaam of periode naar een andere rol.
       - TIJDLIJN / TWEE KOLOMMEN: Als bedrijf/plaats/periode in een linkerkolom en functietitel/beschrijving in een rechterkolom staan (of omgekeerd), reconstrueer per functie één logisch blok: eerst bedrijf + periode + locatie, dan titel, dan beschrijving — ook als de platte tekst door elkaar lijkt.
       - LET OP TITELS: Soms staat de bedrijfsnaam tussen haakjes achter de functietitel (bijv. "Manager Operations (PostNL)"). Split dan: company = PostNL, role = Manager Operations.
       - 'location' (werkplek/regio): alleen vullen als die expliciet bij die rol in de bron staat.
       - 'description': neem de brontekst voor die rol zo getrouw mogelijk over (geen herschrijven). Alleen als de brontekst voor één rol extreem lang is (>900 tekens), mag je die ene description inkorten tot max. ~750 tekens om binnen JSON-limieten te blijven — behoud dan de eerste zinnen met opdrachtgever, scope en concrete resultaten. Voeg GEEN bullets toe tenzij ze in de bron staan.
    6. Extraheer opleidingen (Education) - Neem maximaal 5 opleidingen over. ({ year, degree, school, description }).
    7. CRUCIAAL: VERZIN NOOIT GEGEVENS! Gebruik NOOIT externe logica, hallucinaties of aannames om een profiel "rijker" te maken dan de aangeleverde brontekst. Neem letterlijk alleen over wat er daadwerkelijk getypt staat!
    8. Extraheer cursussen & certificaten (Courses). ({ year, name, institute }).
    9. Extraheer hobby's & interesses (Hobbies). Neem alles over in een array.
    10. Extraheer referenties (References).
    11. Extraheer talenkennis (Languages).
    12. PLAATS ALLE OVERIGE INFORMATIE in "otherText". Wees beknopt!

    BELANGRIJKE REGEL: ONAFHANKELIJK VAN DE TAAL VAN HET CV, JIJ BEANTWOORDT ALLES IN HET VOLLEDIG EN CORRECT NEDERLANDS. Output puur JSON.`
  });

  // Check if it's a docx and extract text locally using mammoth
  const officedocumentPart = parts.findIndex(p => p.inlineData && p.inlineData.mimeType.includes('officedocument'));
  
  if (officedocumentPart !== -1) {
      try {
          console.log("Detecting DOCX file, extracting text using mammoth...");
          const base64Data = parts[officedocumentPart].inlineData?.data || "";
          // Convert base64 to buffer/arraybuffer
          const arrayBuffer = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0)).buffer;
          const result = await mammoth.extractRawText({ arrayBuffer });
          const textContent = result.value;
          
          if (!textContent || textContent.trim() === '') {
              throw new Error("Geen tekst gevonden in dit Word-document.");
          }
          
          // Replace the binary part with a windowed text part (same limits as server-extracted text)
          parts[officedocumentPart] = { text: "Document inhoud:\n" + buildCvTextForModel(textContent) };
      } catch (err) {
          console.error("Mammoth extraction error:", err);
          throw new Error("Dit bestandstype (.docx/Word) kon niet volledig gelezen worden. Probeer het als PDF of kopieer de tekst rechtstreeks.");
      }
  }

  console.log("analyzeUploadedCV called. Model: Azure (proxy)");
  try {
    const response = await ai.models.generateContent({
      model: AI_PROXY_CHAT_MODEL, // Using a known valid model, assume '3' was a typo or future/private
      contents: {
        role: 'user',
        parts: parts
      },
      config: {
        // OCR + CV parsing can take >60s on Azure; allow more time for this call.
        timeoutMs: 180000,
        maxOutputTokens: 12288,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            firstName: { type: Type.STRING },
            lastName: { type: Type.STRING },
            email: { type: Type.STRING },
            phone: { type: Type.STRING },
            linkedin: { type: Type.STRING },
            address: { type: Type.STRING },
            zipCode: { type: Type.STRING },
            city: { type: Type.STRING },
            country: { type: Type.STRING },
            preferredLanguage: { type: Type.STRING },
            dateOfBirth: { type: Type.STRING },
            placeOfBirth: { type: Type.STRING },
            currentRole: { type: Type.STRING },
            yearsExperience: { type: Type.STRING },
            tags: { type: Type.ARRAY, items: { type: Type.STRING } },
            summary: { type: Type.STRING },
            motivation: { type: Type.STRING },
            matchScore: { type: Type.NUMBER },
            matchReason: { type: Type.STRING },
            cultureFit: { type: Type.STRING },
            otherText: { type: Type.STRING },
            hobbies: { type: Type.ARRAY, items: { type: Type.STRING } },
            languages: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  language: { type: Type.STRING },
                  level: { type: Type.STRING }
                }
              }
            },
            references: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  name: { type: Type.STRING },
                  role: { type: Type.STRING },
                  company: { type: Type.STRING },
                  email: { type: Type.STRING },
                  phone: { type: Type.STRING }
                }
              }
            },
            timeline: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  year: { type: Type.STRING },
                  role: { type: Type.STRING },
                  company: { type: Type.STRING },
                  location: { type: Type.STRING },
                  description: { type: Type.STRING }
                }
              }
            },
            education: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  year: { type: Type.STRING },
                  degree: { type: Type.STRING },
                  school: { type: Type.STRING },
                  description: { type: Type.STRING }
                }
              }
            },
            courses: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  year: { type: Type.STRING },
                  name: { type: Type.STRING },
                  institute: { type: Type.STRING }
                }
              }
            }
          },
          required: ["firstName", "tags", "matchScore", "summary", "timeline", "education", "courses"]
        }
      }
    });

    const parsedData = parseGeminiJson(response.text);

    const normalizeCvPersonals = (obj: any) => {
      if (!obj || typeof obj !== 'object') return obj;
      const out: any = { ...obj };

      // Lift nested personal/contact blocks if present (common in NL CV parsing)
      const nested =
        out.personal_info ||
        out.persoonsgegevens ||
        out.contact ||
        out.contactgegevens ||
        out.personalData ||
        null;
      if (nested && typeof nested === 'object') {
        for (const k of Object.keys(nested)) {
          if (out[k] == null) out[k] = nested[k];
        }
      }

      // Common aliases observed in model outputs / OCR flows
      const name =
        out.name ||
        out.fullName ||
        out.naam ||
        out.volledigeNaam ||
        out.kandidaatNaam;
      if ((!out.firstName && !out.lastName) && typeof name === 'string' && name.trim()) {
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) out.firstName = parts[0];
        else {
          out.firstName = parts.slice(0, -1).join(' ');
          out.lastName = parts.slice(-1).join(' ');
        }
      }

      // Email aliases
      if (!out.email) out.email = out.emailAddress || out.eMail || out.mail || out.e_mail;

      // Phone aliases
      if (!out.phone) out.phone = out.telefoon || out.telefoonnummer || out.mobile || out.mobiel;

      // Address/city/zip aliases
      if (!out.address) out.address = out.adres || out.street || out.straat;
      if (!out.zipCode) out.zipCode = out.postcode || out.zip || out.postalCode;
      if (!out.city) out.city = out.woonplaats || out.location || out.plaats;
      if (!out.country) out.country = out.land || out.nationality;

      // LinkedIn aliases
      if (!out.linkedin) out.linkedin = out.linkedin_url || out.linkedinUrl || out.linkedIn;

      // Last-resort regex extraction from the full JSON text (works well for NL CV OCR)
      try {
        const blob = JSON.stringify(out);
        if (!out.email) {
          const m = blob.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
          if (m) out.email = m[0];
        }
        if (!out.phone) {
          const m = blob.match(/(\+31\s?6\s?\d{8}|06[\s-]?\d{8}|\+31\s?\d{1,3}[\s-]?\d{6,8})/);
          if (m) out.phone = m[0].replace(/\s+/g, ' ').trim();
        }
        if (!out.zipCode) {
          const m = blob.match(/\b\d{4}\s?[A-Z]{2}\b/i);
          if (m) out.zipCode = m[0].toUpperCase().replace(/\s+/g, ' ').trim();
        }
      } catch (e) {}

      return out;
    };

    // Recursively clean hard-wraps from all extracted string values 
    const basicTextCleanup = (obj: any): any => {
      if (typeof obj === 'string') {
        let text = obj.replace(/\r\n/g, '\n').trim();
        text = text.replace(/\n{3,}/g, '\n\n');
        return text;
      } else if (Array.isArray(obj)) {
        return obj.map(basicTextCleanup);
      } else if (obj && typeof obj === 'object') {
        const newObj: any = {};
        for (const key in obj) {
          newObj[key] = basicTextCleanup(obj[key]);
        }
        return newObj;
      }
      return obj;
    };

    return basicTextCleanup(
      normalizeCvTimeline(normalizeCvAnalysisResult(normalizeCvPersonals(parsedData))),
    );

  } catch (error) {
    console.error("Gemini Analysis Error:", error);
    throw error;
  }
};

/**
 * Summarizes a raw call transcript into a clean, actionable format.
 */
export const summarizeCallTranscript = async (rawTranscript: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Expert Recruiter Assistant. Lees het volgende transcript van een telefoongesprek.
    Haal de "koetjes en kalfjes" ("hallo, hoe gaat het", afscheid, etc) eruit, en maak een uiterst professionele, scherpe samenvatting van de besproken actiepunten, gegevens en belangrijke details.

    GEGEVENS UIT TELEFOONGESPREK:
    "${rawTranscript}"
    
    Maak gebruik van opsommingstekens (•) per kernpunt. Maximaal 5 beknopte zinnen.
    
    Output JSON:
    {
       "summary": "De geformatteerde samenvatting met • bullets."
    }`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          summary: { type: Type.STRING }
        },
        required: ["summary"]
      }
    }
  });

  const parsed = parseGeminiJson(response.text);
  return parsed.summary || rawTranscript;
};

/* --- EXISTING FUNCTIONS ... (Kept for brevity, assumes they exist) --- */
export const matchTalentPool = async (query: string, candidates: any[]) => {
  // Simplify candidate data to save tokens and focus on relevance
  const simplifiedPool = candidates.map(c => ({
    id: c.id,
    name: c.name || c.candidate?.name || 'Onbekend',
    role: c.candidate?.role,
    experience: c.candidate?.experience,
    tags: c.tags?.map((t: any) => t.name).join(', ')
  }));

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent Froukje.De gebruiker zoekt: "${query}".
    Hier is de huidige talent pool: ${JSON.stringify(simplifiedPool)}.

1. Selecteer de kandidaten die het beste passen.
    2. Geef een korte, gevatte reden waarom je deze selectie hebt gemaakt.
    
    Output JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          matchedIds: {
            type: Type.ARRAY,
            items: { type: Type.STRING }
          },
          reasoning: { type: Type.STRING }
        }
      }
    }
  });

  return parseGeminiJson(response.text);
};

/**
 * Azure OpenAI only gets `response_format: { type: 'json_object' }`; Gemini `responseSchema` is ignored
 * on the server. Models often return `rankings`, `results`, or a single unnamed array key instead of `shortlist`.
 */
function extractScreeningShortlistArray(parsed: unknown): any[] | null {
  if (parsed == null) return null;
  if (Array.isArray(parsed)) {
    return parsed.length > 0 && typeof parsed[0] === 'object' ? parsed : null;
  }
  if (typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;
  const directKeys = [
    'shortlist',
    'rankings',
    'results',
    'matches',
    'candidates',
    'ranked_candidates',
    'rankedCandidates',
    'scores',
    'evaluations',
    'short_list',
    'screening_results',
  ];
  for (const k of directKeys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  const keys = Object.keys(o);
  if (keys.length === 1 && Array.isArray(o[keys[0]])) return o[keys[0]] as any[];
  const nested = o.data ?? o.response ?? o.output;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    for (const k of directKeys) {
      const v = (nested as Record<string, unknown>)[k];
      if (Array.isArray(v)) return v;
    }
  }
  return null;
}

function normalizeScreeningRow(row: any): any | null {
  if (!row || typeof row !== 'object') return null;
  const candidateId =
    row.candidateId ??
    row.candidate_id ??
    row.id ??
    row.uuid ??
    (row.candidate && typeof row.candidate === 'object' ? (row.candidate as any).id : undefined);
  if (candidateId == null || candidateId === '') return null;
  const name = row.name ?? row.naam ?? 'Onbekend';
  const score = Number(row.score ?? row.totalScore ?? row.total_score ?? row.matchScore ?? row.match_score ?? 0);
  const explanation = String(row.explanation ?? row.uitleg ?? row.reason ?? row.summary ?? '');
  let strengths = row.strengths ?? row.pluspunten ?? row.voordelen;
  let risks = row.risks ?? row.minpunten ?? row.nadelen ?? row.zorgen;
  if (!Array.isArray(strengths)) strengths = strengths ? [String(strengths)] : [];
  if (!Array.isArray(risks)) risks = risks ? [String(risks)] : [];
  return {
    candidateId: String(candidateId),
    name: String(name),
    score: Number.isFinite(score) ? score : 0,
    explanation,
    strengths: strengths.map((s: any) => String(s)),
    risks: risks.map((s: any) => String(s)),
  };
}

export const screenTalentPoolBatch = async (
  vacancy: { title: string, description: string },
  candidates: any[],
  limit: number = 5,
  onProgress?: (current: number, total: number, tokensUsed: number) => void
) => {
  // Simplify candidates for token efficiency
  const simplifiedCandidates = candidates.map(c => ({
    id: c.id,
    name: c.name || c.candidate?.name || 'Onbekend',
    role: c.role || c.parsed_cv?.currentRole || c.ai_parsed_data?.currentRole || c.parsed_cv?.role || c.ai_parsed_data?.role || c.ai_parsed_data?.["Function/Role title"] || c.candidate?.role || 'Onbekend',
    skills: (c.skill_set || c.parsed_cv?.tags || c.ai_parsed_data?.tags || c.ai_parsed_data?.["Key Skills"] || c.candidate?.skill_set || [])?.map((t: any) => typeof t === 'string' ? t : t.name).join(', ') || c.tags || c.candidate?.tags || '',
    summary: String(c.cv_summary || c.parsed_cv?.summary || c.ai_parsed_data?.summary || c.ai_parsed_data?.Summary || c.candidate?.cv_summary || '').substring(0, 1000),
    timeline: (c.parsed_cv?.timeline || c.ai_parsed_data?.timeline || c.candidate_client_profiles?.[0]?.ai_parsed_data?.timeline || []).slice(0, 4).map((t: any) => `${t.role || t.title} bij ${t.company} (${t.year || t.period}): ${t.description || ''}`).join(' | '),
    experience_years: c.experience || c.candidate?.experience
  })); // Note: Do NOT filter here — even sparse profiles (no skills/summary yet) must be passed to the AI for scoring

  const chunkSize = 15;
  let allMatches: any[] = [];
  let totalTokensAcc = 0;

  if (onProgress) onProgress(0, candidates.length, 0);

  const chunkCount = Math.ceil(simplifiedCandidates.length / chunkSize) || 0;
  console.log('[screenTalentPoolBatch] start', {
    candidateCount: candidates.length,
    simplifiedCount: simplifiedCandidates.length,
    chunks: chunkCount,
    limit,
  });

  for (let i = 0; i < simplifiedCandidates.length; i += chunkSize) {
    const chunk = simplifiedCandidates.slice(i, i + chunkSize);
    const chunkIndex = Math.floor(i / chunkSize);

    try {
      const response = await ai.models.generateContent({
        model: AI_PROXY_CHAT_MODEL,
        contents: `You are an advanced recruitment AI inside an ATS (Werftools).

Your task is to evaluate candidates against a job vacancy and return a ranked shortlist.

INPUT:
JOB:
Titel: ${vacancy.title}
Beschrijving: ${vacancy.description?.substring(0, 1500)}...

CANDIDATES BATCH:
${JSON.stringify(chunk)}

INSTRUCTIONS:
1. Score each candidate on skill match, experience, seniority, and persona.
2. Calculate total score (0-100).
3. BELANGRIJK: Als expliciete \`skills\` of vaardigheden leeg zijn bij de kandidaat, ZIE DAN DE WERKERVARING/TIMELINE EN EXTRAHEER ZELF DE VAARDIGHEDEN hieruit! Zeg NOOIT "Zonder vaardigheden" als er wel werkervaring staat.
4. Extract STRICT DUTCH: ALL text, explanation, strengths, and risks MUST be written in strict Native Dutch language. Geen Engels!
5. REQUIRED: Provide exactly up to 6 distinct 'strengths' (pluspunten) and up to 6 distinct 'risks' (minpunten) for a comprehensive overview.
6. Return every candidate in this batch with their absolute score.
7. CRITICAL JSON SHAPE: Respond with a single JSON object. The root MUST contain exactly one array property named "shortlist" (ASCII, lowercase). Example start: {"shortlist":[{"candidateId":"<uuid>","name":"...","score":85,"explanation":"...","strengths":[],"risks":[]}]} Do not use other root keys like rankings or results.`,
        config: {
          responseMimeType: "application/json",
          temperature: 0.1,
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              shortlist: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    candidateId: { type: Type.STRING },
                    name: { type: Type.STRING },
                    score: { type: Type.NUMBER },
                    explanation: { type: Type.STRING },
                    strengths: { type: Type.ARRAY, items: { type: Type.STRING } },
                    risks: { type: Type.ARRAY, items: { type: Type.STRING } }
                  },
                  required: ["candidateId", "name", "score", "explanation", "strengths", "risks"]
                }
              }
            }
          }
        }
      });

      const parsed = parseGeminiJson(response.text);
      const rawList = extractScreeningShortlistArray(parsed);
      if (rawList && rawList.length > 0) {
        const normalized = rawList.map(normalizeScreeningRow).filter(Boolean) as any[];
        const n = normalized.length;
        if (n === 0) {
          console.warn('[screenTalentPoolBatch] chunk rows could not be normalized', {
            chunkIndex,
            batchSize: chunk.length,
            topLevelKeys: parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? Object.keys(parsed as object) : [],
          });
        } else {
          if (n < chunk.length) {
            console.warn('[screenTalentPoolBatch] chunk shortlist partial', {
              chunkIndex,
              shortlistLen: n,
              batchSize: chunk.length,
            });
          }
          allMatches.push(...normalized);
        }
      } else {
        console.warn('[screenTalentPoolBatch] chunk missing or invalid shortlist', {
          chunkIndex,
          batchSize: chunk.length,
          topLevelKeys: parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? Object.keys(parsed as object) : [],
        });
      }
      
      if (response.usageMetadata) {
         totalTokensAcc += (response.usageMetadata.totalTokenCount || 0);
      } else {
         totalTokensAcc += 1500;
      }

    } catch (err) {
      console.error('Batch screening chunk failed:', err);
    }

    if (onProgress) {
       onProgress(Math.min(i + chunkSize, simplifiedCandidates.length), simplifiedCandidates.length, totalTokensAcc);
    }
    
    // Rate limit buffer
    await new Promise(r => setTimeout(r, 1500));
  }

  // Finalize
  const rowsBeforeLimit = allMatches.length;
  allMatches = allMatches.sort((a, b) => b.score - a.score).slice(0, limit);
  console.log('[screenTalentPoolBatch] complete', {
    rowsFromAI: rowsBeforeLimit,
    returnedAfterLimit: allMatches.length,
    limit,
  });
  return allMatches;
};

export const generateSourcingStrategies = async (vacancy: { title: string, description: string }, persona: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een expert technical sourcer.
  Vacature: "${vacancy.title}"
Beschrijving: "${vacancy.description?.substring(0, 500)}..."
Persona: "${persona}"

TAAK:
    Genereer 3 creatieve sourcing strategieën om kandidaten te vinden op het open web(LinkedIn, GitHub, etc.).
    Voor elke strategie, creëer ook een 'Target Persona' profiel van de ideale kandidaat die je met deze query vindt.

    Output JSON format:
{
  "strategies": [
    {
      "platform": "LinkedIn",
      "query": "site:linkedin.com/in ...",
      "explanation": "Zoekt naar...",
      "url": "https://...",
      "targetPersona": {
        "bio": "Korte beschrijving van de kandidaat (2 zinnen).",
        "skills": ["Skill 1", "Skill 2"],
        "seniorityLevel": 75, // 0-100
        "seniorityLabel": "Senior",
        "educationLevel": "HBO",
        "currentJobTitles": ["Dev", "Lead"],
        "keywords": ["Java", "Spring"]
      }
    }
  ]
} `,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          strategies: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                platform: { type: Type.STRING },
                query: { type: Type.STRING },
                explanation: { type: Type.STRING },
                url: { type: Type.STRING },
                targetPersona: {
                  type: Type.OBJECT,
                  properties: {
                    bio: { type: Type.STRING },
                    skills: { type: Type.ARRAY, items: { type: Type.STRING } },
                    seniorityLevel: { type: Type.NUMBER },
                    seniorityLabel: { type: Type.STRING },
                    educationLevel: { type: Type.STRING },
                    currentJobTitles: { type: Type.ARRAY, items: { type: Type.STRING } },
                    keywords: { type: Type.ARRAY, items: { type: Type.STRING } }
                  },
                  required: ["bio", "skills", "seniorityLevel", "seniorityLabel", "educationLevel"]
                }
              },
              required: ["platform", "query", "explanation", "url", "targetPersona"]
            }
          }
        }
      }
    }
  });

  return parseGeminiJson(response.text);
};

export const generateMarketInsights = async (jobTitle: string, intakeData: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Expert Arbeidsmarkt Data Analyst. Genereer 10 specifieke kwantitatieve marktinformatie-punten voor de vacature "${jobTitle}" in de huidige arbeidsmarkt.\n    Kijk kritisch naar de MARKT, niet alleen naar de samenvatting in de briefing: "${intakeData}".\n    Beoordeel de rol op basis van schaarste, complexiteit en marktdynamiek. Geef ten minste punten over:\n    1. Verwachte zoekmoeilijkheid / Complexiteit om deze rol in te vullen\n    2. Actueel aanbod van kandidaten (Schaarste)\n    3. Gemiddelde verwachte doorlooptijd (Time to hire in dagen/weken)\n    4. Actueel Benchmarksalaris\n    5. Belangrijkste concurrentie (Wie vist in dezelfde vijver?)\n    6. Primaire drijfveren (Pullfactoren) in de huidige markt\n\n    Geef voor elk punt een korte titel (zoals 'Schaarste' of 'Complexiteit'), een concrete waarde (cijfer, range of korte term), een subtitel / uitleg (max 1 zin) en een trend (bijv +2% of "Stijgend").\nOutput in JSON format.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            value: { type: Type.STRING },
            subtitle: { type: Type.STRING },
            trend: { type: Type.STRING }
          },
          required: ["title", "value", "subtitle"]
        }
      }
    }
  });
  return parseGeminiJson(response.text);
};

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

export const generateStructuredTemplates = async (jobTitle: string, persona: string) => {
  const commonPrompt = `Je bent Froukje, een elite recruitment copywriter. Genereer templates voor de vacature "${jobTitle}".
Gebruik deze persona context: "${persona}".
DOEL: Schrijf content die kandidaten DIRECT activeert. Geen clichés, maar authentieke tekst. Voor elke gevraagde categorie lever precies 4 variaties (rank 1 t/m 4), zet isTopThree op true voor de beste, en geef een froukjeReason in JSON.`;

  // Call 1: LinkedIn & Search String
  const linkedInPromise = ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${commonPrompt}\n\nGenereer:\n1. linkedinPost (4 variaties): Gebruik hooks, witregels, passende emojis en hashtags.\n2. linkedinSearchString: Boolean string voor Sales Navigator obv hard skills.\nOutput JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          linkedinPost: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: { content: { type: Type.STRING }, rank: { type: Type.NUMBER }, isTopThree: { type: Type.BOOLEAN }, froukjeReason: { type: Type.STRING } },
              required: ["content", "rank", "isTopThree", "froukjeReason"]
            }
          },
          linkedinSearchString: { type: Type.STRING }
        },
        required: ["linkedinPost", "linkedinSearchString"]
      }
    }
  });

  await delay(1500); // Prevent 429

  // Call 2: InMail
  const inMailPromise = ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${commonPrompt}\n\nGenereer:\n1. inMail (4 variaties): Prikkelende onderwerpregel, WIIFM focus, max 150 woorden.\nOutput JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          inMail: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: { content: { type: Type.STRING }, rank: { type: Type.NUMBER }, isTopThree: { type: Type.BOOLEAN }, froukjeReason: { type: Type.STRING } },
              required: ["content", "rank", "isTopThree", "froukjeReason"]
            }
          }
        },
        required: ["inMail"]
      }
    }
  });

  await delay(1500); // Prevent 429

  // Call 3: WhatsApp & Meta Ads
  const whatsappPromise = ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${commonPrompt}\n\nGenereer:\n1. whatsappMessage (4 variaties): Max 4 zinnen, geen aanhef sturen.\n2. metaAdsCopy: Korte, visuele tekst met sterke hook.\nOutput JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          whatsappMessage: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: { content: { type: Type.STRING }, rank: { type: Type.NUMBER }, isTopThree: { type: Type.BOOLEAN }, froukjeReason: { type: Type.STRING } },
              required: ["content", "rank", "isTopThree", "froukjeReason"]
            }
          },
          metaAdsCopy: { type: Type.STRING }
        },
        required: ["whatsappMessage", "metaAdsCopy"]
      }
    }
  });

  const [res1, res2, res3] = await Promise.all([linkedInPromise, inMailPromise, whatsappPromise]);

  const linkedInData = parseGeminiJson(res1.text);
  const inMailData = parseGeminiJson(res2.text);
  const whatsappData = parseGeminiJson(res3.text);

  return {
    linkedinPost: linkedInData.linkedinPost || [],
    inMail: inMailData.inMail || [],
    whatsappMessage: whatsappData.whatsappMessage || [],
    linkedinSearchString: linkedInData.linkedinSearchString || '',
    metaAdsCopy: whatsappData.metaAdsCopy || '',
    jobText: [
      { content: "", rank: 1, isTopThree: true, froukjeReason: "N/A" }
    ]
  };
};

export const refineTemplateSpecificly = async (content: string, prompt: string, jobTitle: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT} Je past een specifiek template aan voor de vacature "${jobTitle}".
    HUIDIGE TEKST: "${content}"
    OPDRACHT VAN GEBRUIKER: "${prompt}"
    Zorg dat de kern behouden blijft maar voer de opdracht exact uit.
  ${CLEAN_TEXT_INSTRUCTION} `
  });
  return response.text;
};

export const generateStepCommentary = async (step: string, data: any, jobTitle: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT} Geef een korte, interactieve opmerking(max 40 words) over de stap "${step}" voor de vacature "${jobTitle}". 
    Baseer je op deze data: ${JSON.stringify(data)}.
      ${CLEAN_TEXT_INSTRUCTION} `
  });
  return response.text;
};

export const generateMarketResearch = async (jobTitle: string, intakeData: string, extraPrompt?: string, systemPrompt?: string) => {
  const basePrompt = `Analyseer de arbeidsmarkt voor de rol "${jobTitle}" gebaseerd op deze context: "${intakeData}". Focus op schaarste, salaristrends en drijfveren.`;
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${systemPrompt || basePrompt}
  ${extraPrompt ? `\nEXTRA INSTRUCTIE VAN DE GEBRUIKER: "${extraPrompt}"` : ''}
  ${CLEAN_TEXT_INSTRUCTION}
Format: Geef eerst een "SAMENVATTING"(max 3 punten), daarna de volledige "DETAILS".Output in Nederlands.`
  });
  return response.text;
};
export const generatePersona = async (jobTitle: string, researchData: string, intakeData: string, extraPrompt?: string, systemPrompt?: string) => {
  const basePrompt = `Op basis van deze arbeidsmarkt data: "${researchData}" EN de vacature context: "${intakeData}", creëer een gedetailleerde candidate persona voor de rol "${jobTitle}".
    Neem mee:
- Beschrijvend profiel
  - Competenties
  - Persoonlijkheidstype (Beschrijf werkstijl helder en in begrijpelijke HR taal)
  - Tone of Voice`;
  
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${systemPrompt || basePrompt}
    ${extraPrompt ? `\nEXTRA INSTRUCTIE VAN DE GEBRUIKER: "${extraPrompt}"` : ''}
    - Pullfactoren(waarom zouden ze solliciteren ?)
    - Salarisindicatie(obv markt)
    - Secundaire arbeidsvoorwaarden(optioneel)
    - Job Title Synonyms (Relevante functietitel synoniemen)
    - Stepping Stone Roles (Opstap functies waaruit kandidaten vaak doorgroeien naar deze rol)

    
    Output JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          description: { type: Type.STRING },
          competencies: { type: Type.ARRAY, items: { type: Type.STRING } },
          personalityType: { type: Type.STRING },
          toneOfVoice: { type: Type.STRING },
          pullFactors: { type: Type.ARRAY, items: { type: Type.STRING } },
          salaryRange: { type: Type.STRING },
          secondaryBenefits: { type: Type.STRING },
          jobTitleSynonyms: { type: Type.ARRAY, items: { type: Type.STRING } },
          steppingStoneRoles: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: ["description", "competencies", "personalityType", "toneOfVoice", "pullFactors", "salaryRange", "jobTitleSynonyms", "steppingStoneRoles"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

export const generate_vacancy_from_research = async (jobTitle: string, researchData: string, personaData: string, intakeData: string, structure: string[], clientPrompts?: Record<string, string>) => {
  const sectionInstructions = structure
    .map(s => { 
        const title = s.toLowerCase();
        let promptText = '';
        if (title.includes('intro') || title.includes('over de rol') || title === 'samenvatting') {
            promptText = clientPrompts?.vacatureIntro || '';
        } else if (title.includes('omschrijving')) {
            promptText = clientPrompts?.vacatureFunctieomschrijving || clientPrompts?.vacatureIntro || '';
        } else if (title.includes('werkzaam') || title.includes('taken') || title.includes('verantwoordelijkheden')) {
            promptText = clientPrompts?.vacatureWerkzaamheden || '';
        } else if (title.includes('eisen') || title.includes('vragen') || title.includes('profiel')) {
            promptText = clientPrompts?.vacatureEisen || '';
        } else if (title.includes('voordeel') || title.includes('bieden') || title.includes('aanbod') || title.includes('arbeidsvoorwaard') || title.includes('salaris')) {
            promptText = clientPrompts?.vacatureVoordelen || '';
        } else if (title.includes('overig') || title.includes('informatie') || title.includes('solliciteer') || title.includes('contact')) {
            promptText = clientPrompts?.vacatureOverige || '';
        }
        return promptText ? `Voor sectie "${s}": ${promptText}` : null; 
    })
    .filter(Boolean).join('\n');
  const masterPrompt = clientPrompts?.vacatureGeneratie ||
    `Je bent Froukje, een expert copywriter. Schrijf een wervende vacaturetekst voor de rol "${jobTitle}".`;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${masterPrompt}
    Gebruik deze context:
    - Oorspronkelijke briefing: "${intakeData}"
    - Marktonderzoek: "${researchData}"
    - Persona: "${personaData}"

    De vacature MOET EXACT opgebouwd worden volgens deze secties:
    ${JSON.stringify(structure)}

    Voor elke sectie, genereer overtuigende, professionele tekst in het Nederlands.
    Formatteer de inhoud in strakke HTML (gebruik <p>, <ul>, <li>, <strong> waar relevant).
    GEEN h1 of h2 tags voor de sectietitels, alleen de inhoud.
    ${sectionInstructions ? `\nEXTRA SECTIE-INSTRUCTIES:\n${sectionInstructions}` : ''}

    Output JSON in dit format:
    {
      "sections": [
        { "title": "Intro", "content": "<p>...</p>" }
      ]
    }`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          sections: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                title: { type: Type.STRING },
                content: { type: Type.STRING }
              },
              required: ["title", "content"]
            }
          }
        },
        required: ["sections"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

export const generateToneAndSeo = async (jobTitle: string, keywords: string, tone: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Bepaal de optimale Tone of Voice(${tone}) en SEO strategie voor de vacature "${jobTitle}". 
    Gebruik keywords: "${keywords}".
    Geef concreet advies over woordgebruik, zinslengte en structuur.
  ${CLEAN_TEXT_INSTRUCTION} `
  });
  return response.text;
};

export const generateMarketingStrategy = async (jobTitle: string, researchData: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Ontwikkel een marketing strategie voor "${jobTitle}" gebaseerd op: ${researchData}.
    Focus op:
- Targeting titels(synoniemen) - ALLEEN relevante synoniemen, geen ongerelateerde rollen.
    - Opstap functies(feeder roles)
  - Opleidingsniveaus en studies
    - Skills(hard / soft) - ALLEEN skills die daadwerkelijk in de vacature staan of direct relevant zijn.
    - Interessante concurrenten(waar ze nu likely werken)
  - Aanverwante industrieën
    - Bedrijven met hoog verloop(poach targets)

    STRICT CONSTRAINT: Do not hallucinate skills or keywords that are not implied by the job title or description.

    Output JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          targetingTitles: { type: Type.ARRAY, items: { type: Type.STRING } },
          feederRoles: { type: Type.ARRAY, items: { type: Type.STRING } },
          education: { type: Type.ARRAY, items: { type: Type.STRING } },
          skills: { type: Type.ARRAY, items: { type: Type.STRING } },
          competitors: { type: Type.ARRAY, items: { type: Type.STRING } },
          relatedIndustries: { type: Type.ARRAY, items: { type: Type.STRING } },
          highTurnoverCompanies: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: ["targetingTitles", "feederRoles", "education", "skills", "competitors"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

export const generateMassMarketingAdvice = async (jobTitle: string, researchData: string, strategy: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Geef specifiek advies voor Massa Marketing(Jobboards, Social Ads) voor "${jobTitle}".
    Baseer op strategie: ${strategy}.
    Noem specifieke kanalen(bijv.Indeed, LinkedIn, Tweakers) en geschat budget.
  ${CLEAN_TEXT_INSTRUCTION} `
  });
  return response.text;
};

export const askAIRecruiter = async (question: string, context: any) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT}
Context: ${JSON.stringify(context)}.
Vraag: "${question}"
    Antwoord kort en bondig.`
  });
  return response.text;
};

export const generateProactiveQuestion = async (jobTitle: string, step: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT} De gebruiker is bezig met de stap "${step}" voor vacature "${jobTitle}".
    Stel een proactieve, korte vraag(max 1 zin) om ze te helpen of uit te dagen.`
  });
  return response.text;
};


// --- CV BUILDER HELPERS ---

export const enhanceTextWithAI = async (text: string, type: 'cv_summary' | 'work_description' | 'motivation' | 'general') => {
  const typePrompts = {
    cv_summary: "Herschrijf deze profielschets zodat het krachtiger, professioneler en overtuigender klinkt.",
    work_description: "Maak deze werkomschrijving concreter, resultaatgerichter en gebruik actiewerkwoorden. Gebruik opsommingstekens (•) en zorg dat elke bullet point op een nieuwe regel start (gebruik \\n).",
    motivation: "Herschrijf deze alinea van een motivatiebrief zodat het enthousiast, foutloos en professioneel is.",
    general: "Herschrijf de volgende tekst zodat deze professioneel, beknopt en geschikt is voor een CV. Verbeter grammatica en stijl."
  };

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT} ${typePrompts[type]}
    Originele tekst: "${text}"
    Output alleen de verbeterde tekst, geen uitleg.`
  });
  return response.text;
};

export const parsePasteToProfile = async (pastedText: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Analyseer deze ruwe tekst(van een LinkedIn profiel of CV) en extraheer de gestructureerde data.
  Tekst: "${pastedText.substring(0, 5000)}"
    Output JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          name: { type: Type.STRING },
          title: { type: Type.STRING },
          email: { type: Type.STRING },
          phone: { type: Type.STRING },
          location: { type: Type.STRING },
          summary: { type: Type.STRING },
          workHistory: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                company: { type: Type.STRING },
                role: { type: Type.STRING },
                period: { type: Type.STRING },
                description: { type: Type.STRING }
              }
            }
          },
          education: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                school: { type: Type.STRING },
                degree: { type: Type.STRING },
                year: { type: Type.STRING }
              }
            }
          },
          skills: { type: Type.ARRAY, items: { type: Type.STRING } }
        }
      }
    }
  });
  return parseGeminiJson(response.text);
};

export const generateCandidateCV = async (role: string, experience: string, skills: string, vacancyContext?: string) => {
  // Simplified mock generation or simple prompt
  return {
    summary: `Ervaren ${role} met ${experience} jaar ervaring in ${skills}.`,
    bullets: ["Verantwoordelijk voor X", "Leidde project Y"],
    matchScore: 85,
    matchReason: "Goede ervaring"
  };
};

export const generateCandidateMotivation = async (title: string, company: string, skills: string, tone: string, keywords: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Schrijf een korte, krachtige motivatiealinea voor de functie ${title} bij ${company}.
    Focus op skills: ${skills} en keywords: ${keywords}.
Toon: ${tone}.
    Max 100 woorden.`
  });
  return response.text;
};

export const generateCandidateMotivationV2 = async (
  title: string,
  company: string,
  strongPoints: string[],
  growthPoints: string[],
  initialMotivation: string
) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT} Schrijf de PERFECTE, onweerstaanbare motivatiebrief voor de vacature "${title}" bij "${company}".
    
    KANDIDAAT PROFIEL:
- Sterke punten(verwerk deze als unieke selling points): ${strongPoints.join(', ')}
- Ontwikkelpunten(benoem deze eerlijk maar leerbaar / positief): ${growthPoints.join(', ')}
- Ruwe motivatie van kandidaat: "${initialMotivation}"

OPDRACHT:
    Schrijf een vloeiende, enthousiaste brief(geen bullet points). 
    Zorg dat het professioneel is maar wel met jouw unieke Froukje - flair(toegankelijk, modern).
    Laat de kandidaat klinken als de ideale match die ook zelfbewust is over groeipunten.
  ${CLEAN_TEXT_INSTRUCTION} `
  });
  return response.text;
};

export const generateCompanyDescription = async (name: string, industry: string, keywords: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Schrijf een wervende "Over Ons" tekst voor ${name} in de ${industry} sector.Gebruik keywords: ${keywords}. Max 150 woorden.`
  });
  return response.text;
};

export const generateVacancySuggestions = async (title: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Voor de vacature "${title}", geef:
1. Een lijst van 5 relevante keywords / skills.
    2. Een korte lijst van 3 harde eisen.
    Output JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          keywords: { type: Type.STRING },
          requirements: { type: Type.STRING }
        }
      }
    }
  });
  return parseGeminiJson(response.text);
};

export const generateSiteContent = async (field: string, companyName: string, prompt: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT} Schrijf content voor het veld "${field}" voor de werken - bij site van "${companyName}".
  Instructie: "${prompt}"
    Houd het professioneel, wervend en passend bij de bedrijfscultuur.
  ${CLEAN_TEXT_INSTRUCTION} `
  });
  return response.text;
};

/**
 * Generates a full career site branding configuration based on company parameters.
 */
export const generateFullCareerSite = async (companyName: string, industry: string, vibe: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `You are a professional UX / UI Designer and Employer Branding Expert.
    Design a comprehensive career site configuration for:
  Company: "${companyName}"
Industry: "${industry}"
Vibe / Culture: "${vibe}"

Generate:
1. A color palette(Hex codes) matching the vibe.
    2. Engaging Hero Copy(Title & Subtitle).
    3. A compelling "About Us" section.
    4. A suitable Google Font.
    5. Two fictional but realistic employee stories(testimonials) that fit the industry.

    Output STRICT JSON matching the schema below.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          primaryColor: { type: Type.STRING },
          secondaryColor: { type: Type.STRING },
          headerColor: { type: Type.STRING },
          footerColor: { type: Type.STRING },
          fontColor: { type: Type.STRING },
          font: { type: Type.STRING },
          heroTitle: { type: Type.STRING },
          heroSubtitle: { type: Type.STRING },
          aboutTitle: { type: Type.STRING },
          aboutText: { type: Type.STRING },
          stories: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                employeeName: { type: Type.STRING },
                role: { type: Type.STRING },
                title: { type: Type.STRING },
                quote: { type: Type.STRING },
                // We'll insert mock images in the frontend later, model doesn't need to guess URLs
              },
              required: ["employeeName", "role", "title", "quote"]
            }
          }
        },
        required: ["primaryColor", "heroTitle", "aboutText", "stories"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

/**
 * Conversational AI to update site config based on chat messages.
 */
export const updateCareerSiteConfig = async (currentConfig: any, userPrompt: string) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT} Je bent een UI / UX expert.De gebruiker wil de career site aanpassen.
    
    HUIDIGE CONFIGURATIE(JSON):
    ${JSON.stringify(currentConfig)}

    VERZOEK VAN GEBRUIKER:
"${userPrompt}"

    Jouw taak:
1. Begrijp wat de gebruiker wil veranderen.
    2. Geef een JSON object terug met * alleen * de velden die aangepast moeten worden.
    3. Specifieke mapping:
- Als de gebruiker praat over "header tekst kleur", "header font"(kleur), gebruik veld: 'headerTextColor'.
       - Als de gebruiker praat over "footer tekst kleur", gebruik veld: 'footerTextColor'.
       - Als de gebruiker sociale media links wil toevoegen(LinkedIn, Instagram), voeg toe aan 'socialLinks' array.
    
    Output STRICT JSON.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          primaryColor: { type: Type.STRING },
          secondaryColor: { type: Type.STRING },
          headerColor: { type: Type.STRING },
          headerTextColor: { type: Type.STRING },
          footerColor: { type: Type.STRING },
          footerTextColor: { type: Type.STRING },
          fontColor: { type: Type.STRING },
          font: { type: Type.STRING },
          heroTitle: { type: Type.STRING },
          heroSubtitle: { type: Type.STRING },
          aboutTitle: { type: Type.STRING },
          aboutText: { type: Type.STRING },
          socialLinks: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                platform: { type: Type.STRING, enum: ['linkedin', 'instagram', 'facebook', 'twitter', 'youtube', 'website'] },
                url: { type: Type.STRING }
              }
            }
          }
        }
      }
    }
  });
  return parseGeminiJson(response.text);
};

/**
 * Generates full vacancy details based on a title and extra instructions.
 */

/**
 * Generates full vacancy details based on a title and extra instructions.
 */
export const generateVacancyDetails = async (jobTitle: string, extraInstructions: string, clientPrompts?: Record<string, string>) => {
  const masterPrompt = clientPrompts?.vacatureAutoFill ||
    `${FROUKJE_PROMPT} Je bent een Senior Recruiter. Schrijf een volledige vacature intake op basis van een titel.`;
  const eisenExtra = clientPrompts?.vacatureEisen ? `\nExtra instructie voor EISEN: ${clientPrompts.vacatureEisen}` : '';
  const voordelenExtra = clientPrompts?.vacatureVoordelen ? `\nExtra instructie voor VOORDELEN: ${clientPrompts.vacatureVoordelen}` : '';

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${masterPrompt}

  FUNCTIETITEL: "${jobTitle}"
    EXTRA INSTRUCTIES: "${extraInstructions || 'Geen'}"
    ${eisenExtra}${voordelenExtra}

    Jouw taak:
1. Bedenk een goede interne job code(bv.AFDELING - JAAR-001).
    2. Bepaal het waarschijnlijke workplace type(On - site, Hybrid, Remote).
    3. Bedenk een logische locatie(bv.Randstad).
    4. Schrijf een wervende 'description'(Over de rol).Gebruik < p > tags voor paragrafen.
    5. Schrijf concrete 'requirements'(Wat vragen wij).GEBRUIK ALTIJD EEN HTML LIJST: <ul><li>...</li></ul >.Geen markdown *, geen platte tekst.
    6. Schrijf aantrekkelijke 'benefits'(Wat bieden wij).GEBRUIK ALTIJD EEN HTML LIJST: <ul><li>...</li></ul >.Geen markdown *, geen platte tekst.
    7. Schat een marktconform salaris in (min / max / currency / period).
    8. Schat het waarschijnlijke ervaringsniveau en opleidingsniveau in.

    Output STRICT JSON:
{
  "jobCode": "...",
    "workplaceType": "On-site" | "Hybrid" | "Remote",
      "location": "...",
        "description": "<p>...</p>",
          "requirements": "<ul><li>...</li></ul>",
            "benefits": "<ul><li>...</li></ul>",
              "salary": { "min": 3000, "max": 5000, "currency": "EUR", "period": "Monthly" },
  "experience": "Junior" | "Medior" | "Senior" | "Expert",
    "education": "MBO" | "HBO" | "WO" | "PhD",
      "employmentType": "Fulltime" | "Parttime" | "Freelance" | "Internship"
}

    ${CLEAN_TEXT_INSTRUCTION} `,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          jobCode: { type: Type.STRING },
          workplaceType: { type: Type.STRING, enum: ['On-site', 'Hybrid', 'Remote'] },
          location: { type: Type.STRING },
          description: { type: Type.STRING },
          requirements: { type: Type.STRING },
          benefits: { type: Type.STRING },
          salary: {
            type: Type.OBJECT,
            properties: {
              min: { type: Type.NUMBER },
              max: { type: Type.NUMBER },
              currency: { type: Type.STRING },
              period: { type: Type.STRING }
            }
          },
          experience: { type: Type.STRING, enum: ['Junior', 'Medior', 'Senior', 'Expert'] },
          education: { type: Type.STRING, enum: ['MBO', 'HBO', 'WO', 'PhD'] },
          employmentType: { type: Type.STRING, enum: ['Fulltime', 'Parttime', 'Freelance', 'Internship'] }
        },
        required: ["jobCode", "workplaceType", "location", "description", "requirements", "benefits", "salary", "experience", "education", "employmentType"]
      }
    }
  });
  return parseGeminiJson(response.text);
};

export const updateCareerSiteSections = async (currentSections: any[], userPrompt: string, brandingConfig?: any) => {
  const brandContext = brandingConfig ? `
    HULLIGE BRANDING CONFIG:
    - Bedrijfsnaam/Titel: ${brandingConfig.heroTitle}
    - Over ons: ${brandingConfig.aboutText}
    - Primaire Kleur: ${brandingConfig.primaryColor}
    - Secundaire Kleur: ${brandingConfig.secondaryColor}
  ` : '';

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `Je bent een Senior Web Designer en Creatieve Copywriter voor een Recruitment Site Builder.
    De gebruiker stelt een vraag of geeft een commando via chat om de webpagina aan te passen.
    ${brandContext}
    
    HUIDIGE SECTIES APP-STATE (JSON Array):
    ${JSON.stringify(currentSections, null, 2)}

    VERZOEK VAN GEBRUIKER: "${userPrompt}"
    
    JOUW TAAK: 
    Genereer of muteer de pagina secties. JIJ BENT VERANTWOORDELIJK VOOR DE CONTENT! 
    Als de gebruiker vraagt om "een sectie over onze startup mentaliteit", schrijf jij KRACHTIGE, WERVEND, PROFESSIONELE copy in de 'title', 'subtitle', en 'content.text'.
    Wees creatief, gebruik overtuigende marketingtaal en zorg dat de opmaak er prachtig uitziet met de juiste achtergrondkleuren (gebruik HEX codes indien nodig die passen bij het merk).
    
    EIGENSCHAPPEN DIE JE KUNT INSTELLEN PER SECTIE:
    - Toegestane 'type': 'hero', 'text_image', 'features', 'vacancies', 'header', 'footer', 'gallery', 'faq', 'stats', 'video', 'text_only', 'team', 'pricing', 'contact'.
    - Gemeenschappelijk: 'title', 'subtitle', 'styles' ({ backgroundColor, textColor, align, padding, buttonColor, borderRadius, shadow })
    - 'hero': title (catchy), subtitle (wervend), content.imageUrl (gebruik Unsplash/Pexels mockup urls als je niets hebt), buttonText
    - 'features': Gebruik dit voor kernwaarden of USP's.
    - 'text_image', 'text_only': Gebruik 'content.text' voor mooie paragrafen met <br/> of <strong> HTML formatting.
    - 'vacancies': Title ("Onze Openstaande Rollen")
    - 'stats': Voeg indrukwekkende cijfers toe onder 'content.items' [{ label, value }]
    - 'video': Voeg een 'content.videoUrl' toe (bijv. youtube URL).
    
    LET OP: Retourneer ALLEEN de volledige, NIEUWE JSON ARRAY van de secties. Structureer het exact als input. GEEN Markdown, GEEN extra tekst!`,
    config: {
      responseMimeType: "application/json"
    }
  });

  const parsed = parseGeminiJson(response.text);
  return Array.isArray(parsed) ? parsed : [];
};

export const askFroukjeDataAnalyst = async (
  prompt: string,
  vacancies: any[],
  candidates: any[],
  messageHistory: { role: 'user' | 'model', content: string }[] = [],
  aiName: string = 'Froukje',
  todos: any[] = [],
  colleagues: any[] = []
) => {
  try {
    /** Keep payloads small — large JSON + Gemini often hits 408 / deadline exceeded */
    const MAX_VAC = 280;
    const MAX_CAND = 280;
    const vacSource = Array.isArray(vacancies) ? vacancies.slice(0, MAX_VAC) : [];
    const candSource = Array.isArray(candidates) ? candidates.slice(0, MAX_CAND) : [];

    // Minimal data extraction to prevent token overflow
    const minimalVacancies = vacSource.map(v => ({
      id: v.id,
      title: v.title,
      status: v.status,
      createdAt: v.createdAt || v.created_at,
      location: v.location || v.data?.location,
      department: v.data?.department
    }));

    const minimalCandidates = candSource.map(c => {
      const parsedData = c.ai_parsed_data || c.parsed_cv || {};
      const candidateObj = c.candidate || c || {};

      let skillsStr = '';
      if (Array.isArray(parsedData.tags)) skillsStr = parsedData.tags.join(', ');
      else if (Array.isArray(c.tags)) skillsStr = c.tags.map((t: any) => t.name || t).join(', ');
      else if (c.skills) skillsStr = Array.isArray(c.skills) ? c.skills.join(', ') : c.skills;

      return {
        id: candidateObj.id || c.id,
        name: candidateObj.name || c.name || 'Onbekend',
        role: parsedData.currentRole || parsedData.role || candidateObj.role || 'Onbekend',
        city: c.city || c.location || parsedData.city,
        status: c.status,
        skills: skillsStr,
        experience_years: parsedData.yearsExperience || c.experience_years || candidateObj.experience,
        createdAt: c.createdAt || c.created_at,
        vacancyTitle: c.vacancy?.title
      };
    });
    const minimalTodos = todos.map(t => ({
      id: t.id,
      title: t.title,
      status: t.status,
      dueDate: t.due_date,
      assignee: t.assignee?.name || 'Onbekend',
      creator: t.creator?.name || 'Onbekend',
      vacancy: t.vacancy?.title
    }));

    const minimalColleagues = colleagues.map(c => ({
      id: c.id,
      name: c.name || 'Onbekend',
      role: c.role
    }));

    const systemInstruction = `Je bent ${aiName}, een geniale HR Data Analist en Assistant voor Werftools met een passie voor visuele data en managementinformatie.
Jouw taak is om inzicht te geven op basis van vacatures, kandidaten, taken (todos) en recruiters.
Geef antwoord op de vraag van de gebruiker ('user') gebaseerd op de verstrekte data.
Spreek de gebruiker professioneel en vlot (jij/jou) aan in het Nederlands, met gebruik van relevante emoji's.

Als een manager of beheerder vraagt naar de werkdruk van recruiters (bijv. "hoeveel openstaande todos hebben mijn recruiters?"), gebruik dan de TODO data om antwoord te geven en breng de rollen in kaart (bijv. hoeveel taken per recruiter).

BELANGRIJKE REGEL VOOR KANDIDATEN: Als je het over een specifieke kandidaat hebt of er één aanbeveelt, NOEM NOOIT HUN RUWE ID. Gebruik in plaats daarvan ALTIJD de naam van de kandidaat, opgemaakt als een speciale Markdown link met als URL 'candidate:[ID]'. Bijvoorbeeld: [Jan de Vries](candidate:1234-abcd) of [Piet Bakker](candidate:5678-efgh).

Jij bent EXTREEM visueel ingesteld. Voor VRIJWEL ELKE vraag waar je data, aantallen, of verdelingen analyseert (óók workloads van recruiters!), MOET je een 'chart' (bar, pie, of line) en/of een 'table' teruggeven om het visueel en aantrekkelijk te maken!
Je MOET altijd een JSON object teruggeven met de volgende structuur:
{
  "text": "Jouw vlotte, enthousiaste antwoord in Markdown opmaak.",
  "chart": null of {
    "type": "bar" | "pie" | "line",
    "title": "Duidelijke titel",
    "data": [{"name": "Label 1", "value": 10}, {"name": "Label 2", "value": 20}]
  },
  "table": null of {
    "headers": ["Kolom 1", "Kolom 2"],
    "rows": [["Rij 1 Waarde 1", "Rij 1 Waarde 2"], ["Rij 2 Waarde 1", "Rij 2 Waarde 2"]]
  }
}

Gebruik ALTIJD een 'chart' of 'table' zodra er meer dan 2 datapunten te vergelijken zijn.

DATA:
--- VACATURES (${minimalVacancies.length}) ---
${JSON.stringify(minimalVacancies)}

--- KANDIDATEN (${minimalCandidates.length}) ---
${JSON.stringify(minimalCandidates)}

--- RECRUITERS / TEAM (${minimalColleagues.length}) ---
${JSON.stringify(minimalColleagues)}

--- TAKEN / TODOS (${minimalTodos.length}) ---
${JSON.stringify(minimalTodos)}`;

    const formattedHistory: any[] = messageHistory.map(msg => ({
      role: msg.role,
      parts: [{ text: msg.content }]
    }));

    formattedHistory.push({ role: 'user', parts: [{ text: prompt }] });
    
    const response = await ai.models.generateContent({
      model: AI_PROXY_CHAT_MODEL,
      systemInstruction: systemInstruction,
      contents: formattedHistory,
      config: {
        responseMimeType: "application/json",
      }
    });

    return parseGeminiJson(response.text);

  } catch (error: any) {
    console.error("Froukje Data Analyst Error:", error);
    const msg = String(error?.message || error || '');
    const timedOut =
      msg.includes('408') ||
      msg.toLowerCase().includes('timeout') ||
      msg.toLowerCase().includes('deadline') ||
      msg.toLowerCase().includes('timed out');
    return {
      text: timedOut
        ? 'Het duurt te lang om een antwoord te krijgen (timeout). Probeer het zo meteen opnieuw met een kortere vraag, of wacht even als de dienst druk is.'
        : 'Oeps, er ging iets mis bij het analyseren van de data. Probeer je vraag anders te formuleren of probeer het later opnieuw.',
      chart: null,
    };
  }
};

/**
 * Generates an AI avatar using Imagen
 */
export const generateAIAvatar = async (name: string, company: string = '') => {
  try {
    const response = await ai.models.generateImages({
      model: 'imagen-3.0-generate-001',
      prompt: `A friendly, professional, and trustworthy portrait of a recruiter named ${name}. ${company ? `They work at ${company}. ` : ''} Modern corporate style, clean white or light grey background, high quality, realistic but slightly stylized as a modern avatar.`,
      config: {
        numberOfImages: 1,
        aspectRatio: '1:1',
        outputMimeType: 'image/jpeg',
      }
    });

    if (response.generatedImages && response.generatedImages.length > 0) {
      // The SDK returns imageBytes as a base64 string
      const base64 = response.generatedImages[0].image.imageBytes;
      return `data: image / jpeg; base64, ${base64} `;
    }
    throw new Error("Geen afbeelding gegenereerd.");
  } catch (err: any) {
    console.error("Error generating avatar:", err);
    throw new Error("Genereren van avatar is mislukt.");
  }
};

export const askGDPRSpecialist = async (
  query: string,
  logs: any[],
  history: { role: 'user' | 'model', content: string }[] = []
): Promise<string> => {
  try {
    // Create a compact version of logs for the prompt
    const compactLogs = logs.map(l => ({
      t: l.timestamp,
      a: l.actor,
      type: l.actorType,
      act: l.action,
      ent: l.entity,
      sev: l.severity
    }));

    const systemInstruction = `Je bent de Werfplaats AVG/GDPR Compliance Specialist.
Jouw taak is om de verstrekte Audit Trail logs (JSON) en processen te analyseren op strikte naleving van de AVG/GDPR privacywetgeving voor werving & selectie.
Je bent zeer scherp op datalekken, ongeautoriseerde toegang en bewaartermijnen.

BELANGRIJK BELEID: CV's mogen UITSLUITEND in de talentpool voorkomen of worden toegevoegd wanneer:
1. Een kandidaat zelf direct solliciteert.
2. Een kandidaat zijn/haar CV expliciet instuurt via e-mail.
3. Een kandidaat zich aanmeldt via de website (site editor).
Handmatige toevoegingen of data-scraping waarbij de kandidaat geen actieve actie heeft ondernomen zijn NIET toegestaan onder onze strikte AVG richtlijnen. Wees hier extreem streng op als je log-activiteiten beoordeelt.

Geef antwoord op vragen van de gebruiker. Als je de logs scant, wijs op potentiële risico's en geef aanbevelingen.

- Formatteer je antwoord professioneel maar vlot in heldere Markdown.
- Gebruik icoontjes zoals 🛡️, ⚠️, ✅ waar toepasselijk.
- Geef bij het scannen altijd een "AVG / GDPR Compliance Rapportage" structuur.

HUIDIGE AUDIT LOGS (${compactLogs.length} items):
${JSON.stringify(compactLogs)}`;

    const formattedHistory: any[] = history.map(msg => ({
      role: msg.role,
      parts: [{ text: msg.content }]
    }));

    formattedHistory.push({ role: 'user', parts: [{ text: query }] });
    
    const response = await ai.models.generateContent({
      model: AI_PROXY_CHAT_MODEL,
      systemInstruction: systemInstruction,
      contents: formattedHistory,
      config: {
        temperature: 0.2
      }
    });

    return response.text;
  } catch (error) {
    console.error("GDPR Specialist Error:", error);
    return "Oeps, kon de logs momenteel niet scannen. Probeer het later opnieuw.";
  }
};

export const gemini_analyze_tender = async (input: string | { base64: string, mimeType: string }): Promise<any> => {
  try {
    const prompt = `Je bent een expert recruitment consultant en AI specialist in het ontleden van tenders, opdrachten en klant-aanvragen voor vacatures.
Analyseer de bijgevoegde tekst of het document GRONDIG en extraheer alle beschikbare informatie zodat een recruitment team direct een volledige vacature kan opstellen.

KRITIEKE REGELS:
- VERZIN NIETS! Extraheer alleen informatie die LETTERLIJK in de tekst staat.
- Schrijf de description als een volledige, wervende vacaturetekst (meerdere paragrafen) met alle relevante details.
- Neem zoveel mogelijk specifieke informatie mee: competenties, vereiste certificaten, sector-specifieke kennis, werkomgeving.
- Gebruik professioneel wervend Nederlands in de description.

Geef antwoord in strict JSON formaat:

{
  "title": "Exacte functietitel uit het document",
  "description": "UITGEBREIDE wervende vacaturetekst (minimaal 3 paragrafen). Neem op: context/organisatie, de rol en impact, dagelijkse werkzaamheden, vereiste profiel. Formatteer in HTML met <p> <ul> <li> <strong> <br>. NIET te beknopt!",
  "requirements": ["Concreet vereiste skill/certificaat/ervaring 1", "Vereiste 2", ...],
  "responsibilities": ["Verantwoordelijkheid 1", "Taak/opdracht 2", ...],
  "benefits": ["Voordeel of arbeidsvoorwaarde 1", ...],
  "location": "Locatie/standplaats uit het document (Alleen als benoemd)",
  "hoursPerWeek": "Aantal uren per week (bijv. 32-40 uur, of leeg)",
  "salaryMin": 0,
  "salaryMax": 0,
  "planningDate": "YYYY-MM-DD (laat leeg indien niet benoemd)",
  "closingDate": "YYYY-MM-DD (deadline aanvraag, laat leeg indien niet benoemd)",
  "startDate": "YYYY-MM-DD (verwachte startdatum, laat leeg indien niet benoemd)",
  "contractType": "Vast / ZZP / Interim / Detachering etc. (Alleen als benoemd)",
  "contractDuration": "Bijv. 6 maanden, 1 jaar, onbepaalde tijd (Alleen als benoemd)",
  "education": "Vereist opleidingsniveau (HBO/WO etc., alleen als benoemd)",
  "experience": "Gewenste werkervaring in jaren of niveau (alleen als benoemd)",
  "companyName": "Naam van de opdrachtgever/organisatie (alleen als vermeld)",
  "companyDescription": "Korte beschrijving van de organisatie/klant (alleen als vermeld)"
}`;

    const parts: any[] = [];
    if (typeof input === 'string') {
      parts.push({ text: `INSTRUCTIE:\n${prompt}\n\nTEKST:\n"""\n${input}\n"""` });
    } else {
      parts.push({ text: prompt });
      parts.push({
        inlineData: {
          mimeType: input.mimeType,
          data: input.base64
        }
      });
    }

    const response = await ai.models.generateContent({
      model: AI_PROXY_CHAT_MODEL,
      contents: {
        role: 'user',
        parts: parts
      },
      config: {
        responseMimeType: "application/json",
      }
    });

    const resultText = response.text || "{}";
    return JSON.parse(resultText);
  } catch (error) {
    console.error("Error analyzing tender:", error);
    return null;
  }
};

/**
 * Generates a checklist for a call based on a topic
 */
export const generateCallChecklist = async (topic: string): Promise<string[]> => {
  try {
    const response = await ai.models.generateContent({
      model: AI_PROXY_CHAT_MODEL,
      contents: `Je bent een Expert Recruiter. Maak een professionele, beknopte checklist voor een telefoongesprek met een kandidaat over het volgende onderwerp:
      
      ONDERWERP: "${topic}"
      
      Geef de output strict als een JSON array van strings (max 10 items). Elk item moet een duidelijke, actiegerichte stap of vraag zijn die in het gesprek behandeld moet worden. Formatteer in helder en zakelijk Nederlands.
      
      Voorbeeld output: Let op, gebruik NOOIT dubbele quotes in de strings zelf.
      ["Introductie en ijsbreker", "Bespreek huidige rol en verantwoordelijkheden", "Vraag naar verwachtingen en wensen"]`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.STRING
          }
        }
      }
    });

    return parseGeminiJson(response.text);
  } catch (error) {
    console.error("Error generating call checklist:", error);
    return ["Fout bij laden AI checklist", "Probeer het opnieuw"];
  }
};

/**
 * Generates an actionable optimization plan based on vacancy performance and funnel metrics
 */
export const generateVacancyAnalyticsOptimization = async (vacancyDetails: any, metrics: any) => {
  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${FROUKJE_PROMPT} Analyseer de recruitment prestaties voor de vacature "${vacancyDetails?.title || 'Onbekend'}".
    
    VACATURE:
    Titel: ${vacancyDetails?.title || 'Onbewerkt'}
    Beschrijving: ${vacancyDetails?.description?.substring(0, 1000) || 'Geen tekst'}

    ANALYTICS:
    Totaal kandidaten: ${metrics.totalApplicants}
    Time to Hire (dagen): ${metrics.timeToHire}
    Conversie Rate (%): ${metrics.conversionRate}
    Actieve Pijplijn: ${metrics.activePipeline}
    Funnel: ${JSON.stringify(metrics.funnelData)}

    DOEL:
    Geef minstens 3 zeer concrete, actiegerichte verbetersuggesties (optimizations) om de flow, conversie of time-to-hire te verbeteren gebaseerd op de cijfers en de tekst.
    Gebruik een aanmoedigende, tikkeltje scherpe tone-of-voice. Wees inzichtelijk. Als conversie laag is of drop-off in een bepaalde fase hoog is, benoem dat specifiek.
    
    Geef je antwoord strict als JSON:
    {
      "commentary": "Korte, vlotte, Froukje-style inleiding over wat je ziet in de data.",
      "suggestions": [
        {
          "title": "Titel van de suggestie",
          "description": "Concrete uitleg wat er aan de hand is en wat we moeten doen",
          "impact": "High | Medium | Low"
        }
      ]
    }`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          commentary: { type: Type.STRING },
          suggestions: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                title: { type: Type.STRING },
                description: { type: Type.STRING },
                impact: { type: Type.STRING, enum: ['High', 'Medium', 'Low'] }
              },
              required: ["title", "description", "impact"]
            }
          }
        },
        required: ["commentary", "suggestions"]
      }
    }
  });

  return parseGeminiJson(response.text);
};

export const translateCVData = async (pages: any[], targetLang: string) => {
  const prompt = `Je krijgt een JSON object die de data structuur is van een CV in een visual editor.
Mijn doel is het volledig te vertalen naar de volgende taal: "${targetLang}".
  
INSTRUCTIES:
1. BEHOUD de exacte JSON structuur (arrays, objects) en eigenschappen (zoals id, type, style).
2. VERTAAL ALLEEN de string values die tekst voor in het CV zijn. Denk aan 'text', 'title', 'subtitle', 'role', 'company', 'content', 'description', 'period' of array items met tekst.
3. VERTAAL GEEN keys! Vertaal ook NIET de values van styling, classNames, of andere meta-data.
4. Output uitsluitend in JSON formaat (een array van arrays), exact overkoepelend als de input.`;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: [
      { role: 'user', parts: [{ text: prompt }, { text: JSON.stringify(pages) }] }
    ],
    config: { responseMimeType: "application/json" }
  });

  return parseGeminiJson(response.text);
};

export const generateBatchWorkExperiences = async (workHistoryStr: string, extraPrompt?: string) => {
  const basePrompt = `Je bent 'Froukje', een AI recruiter. Herschrijf de onderstaande werkervaringen van een kandidaat.
Zorg dat alle bulletpoints beginnen met sterke, actieve werkwoorden in de ik-vorm of onbepaald (bijv. 'Implementeren van', 'Coördineren van', 'Verantwoordelijk voor').
Houd de originele inhoud intact, maar maak het professioneler, actiever en bondiger.
Zorg dat elke werkervaring als output exact in deze JSON structuur wordt teruggegeven: [{"company": "naam", "role": "rol", "period": "periode", "description": "Herschreven tekst in bullets (zonder HTML maar met line breaks of bullet symbolen)"}]`;

  const finalPrompt = extraPrompt 
    ? `${extraPrompt}\n\nZorg dat elke werkervaring als output exact in deze JSON structuur wordt teruggegeven: [{"company": "naam", "role": "rol", "period": "periode", "description": "Herschreven tekst in bullets (zonder HTML)"}]`
    : basePrompt;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: `${finalPrompt}\n\nHier is de data:\n${workHistoryStr}`
  });
  return parseGeminiJson(response.text);
};

export const translateFullCv = async (cvData: any, targetLanguage: string) => {
  const langName = targetLanguage === 'en' ? 'Engels (English)' : targetLanguage === 'de' ? 'Duits (German)' : targetLanguage === 'fr' ? 'Frans (French)' : 'Nederlands (Dutch)';
  const prompt = `Je bent een professionele vertaler en recruiter. Vertaal het volgende CV Data (JSON) bestand naar het ${langName}.
Het is CRUCIAAL dat je EXACT DEZELFDE JSON STRUCTUUR aanhoudt. Vertaal alleen de inhoudelijke tekstwaarden (zoals summary, motivation, description, title, role, company, school, degree, listValues, etc).
Laat de veldnamen (keys) ALTIJD exact zoals ze zijn in het originele JSON object.
Geef je antwoord 100% als pure JSON.

Hier is de CV data:`;

  const response = await ai.models.generateContent({
    model: AI_PROXY_CHAT_MODEL,
    contents: prompt + "\n\n" + JSON.stringify(cvData)
  });
  return parseGeminiJson(response.text);
};

export const generateMotivatieOmbouwBuddy = async (cvData: any, vacancy: any, userToelichting: string, mode: 'alles' | 'motivatie' | 'eisen' | 'wensen' | 'correctie' | 'expertise', currentText?: string) => {
  const systemInstruction = `Je bent de Motivatie Ombouw Buddy. Beschrijving: Corrigeert teksten, schrijft motivaties, en geeft toelichtingen.
Je bent een veelzijdige assistent die de volgende taken uitvoert:

1. **Tekst correctie**:
   - Corrigeer teksten zoals profielschetsen, motivaties en andere geschreven stukken.
   - Verwijder misplaatste komma's, extra spaties en onnodige linebreaks.
   - Zorg voor een consistente, nette opmaak zonder de inhoud te veranderen.
   - Stuur alleen de aangepaste tekst terug, zonder toelichting of extra tekst.

2. **Toelichting functie-eisen en wensen**:
   - Zoek zelf proactief in de vacaturetekst naar de specifieke eisen (knock-out) of wensen (nice-to-haves).
   - Voor elke uit de vacature gehaalde functie-eis of wens geef je een toelichting in de ik-vorm, met concrete voorbeelden van werkzaamheden, functies en bedrijven uit het CV van de kandidaat.
   - Begin met voorbeelden van werk bij overheidsinstanties indien mogelijk.
   - Zorg dat de toelichting kort en bondig is en aansluit bij de functie.
   - Elke toelichting wordt geretourneerd in het volgende format:
     "Eis/Wens <nummer>: <omschrijving eis of wens>
     <toelichting op eis of wens>"
   - Als een functie-eis vraagt naar een aantal jaar ervaring, begin de toelichting met hoeveel jaar de kandidaat hierin ervaring heeft.
   - Gebruik geen opmaak in de toelichtingen.
   - Gebruik als leidraad de eigen toelichting van de gebruiker en vul aan met informatie uit het CV indien nodig.
   - Zorg dat de antwoorden relevant zijn voor de functie waarvoor de toelichting wordt meegestuurd.

3. **Motivatie schrijven**:
   - Schrijf twee alinea's voor een motivatiebrief op basis van de functietitel, vacaturetekst, CV en eventuele motivatie.
   - In de eerste alinea beschrijf je waarom de persoon de rol wil, met nadruk op relevante werkervaring, functies en overheidsinstanties.
   - In de tweede alinea, beginnend met "Wat mij tekent als persoon…", focus je op karaktereigenschappen.
   - Gebruik korte, duidelijke zinnen en begin soms een zin met "ik", maar niet de eerste zin van een alinea.
   - Zorg dat de inhoud aansluit bij de vacature en de rol.
   - Gebruik de eigen toelichting van de gebruiker als leidraad en vul aan met informatie uit het CV indien nodig.
   - Voeg geen headers boven de alinea's.

4. **Expertise en competenties**:
   - Genereer een bullet list met expertisegebieden en competenties op basis van de vacature en het CV.
   - Geef punten die relevant zijn voor de functie.
   - Vermijd uitleg en beperk je tot de opsomming.`;

  // --- Start Settings Logic ---
  const { data: { session } } = await supabase.auth.getSession();
  let clientId = session?.user?.user_metadata?.client_id;
  
  if (!clientId && session?.user?.id) {
     const { data: profile } = await supabase.from('profiles').select('client_id').eq('id', session.user.id).single();
     if (profile?.client_id) clientId = profile.client_id;
  }
  
  let customPrompt = '';
  let provider = 'azure';
  let openaiKey = '';
  
  if (clientId) {
      const settings = await supabase_get_client_settings(clientId);
      if (settings) {
          const apiSettings = settings.ai_api_settings || {};
          const promptsSettings = settings.ai_prompts_settings || {};
          
          let promptKey = '';
          if (mode === 'correctie') promptKey = 'buddyCorrectie';
          if (mode === 'motivatie') promptKey = 'buddyMotivatie';
          if (mode === 'eisen') promptKey = 'buddyEisen';
          if (mode === 'wensen') promptKey = 'buddyEisen'; // Share prompt with eisen
          if (mode === 'expertise') promptKey = 'buddyExpertise';
          
          if (promptKey) {
              customPrompt = promptsSettings[promptKey] || '';
              const specificProvider = promptsSettings[`${promptKey}Provider`];
              if (specificProvider && specificProvider !== 'default') {
                  provider = specificProvider;
              } else {
                  provider = apiSettings.provider || 'azure';
              }
          }
          openaiKey = apiSettings.chatgpt || '';
      }
  }
  // --- End Settings Logic ---

  let userPrompt = `Hieronder de beschikbare context:
Vacature: ${JSON.stringify(vacancy)}
Kandidaat CV: ${JSON.stringify(cvData.personalInfo)} \n ${JSON.stringify(cvData.workHistory)} \n ${JSON.stringify(cvData.education)}
Eigen Toelichting Gebruiker: "${userToelichting}"
`;

  let activeSystemInstruction = systemInstruction;

  if (customPrompt) {
      activeSystemInstruction = customPrompt;
      if (mode === 'correctie') {
          userPrompt += `\nTe corrigeren tekst: "${currentText}"`;
      }
  } else {
      if (mode === 'correctie') {
          userPrompt += `\nOpdracht: Voer een Tekst correctie uit op de volgende tekst volgens instructie 1. Tekst: "${currentText}"`;
      } else if (mode === 'eisen') {
          userPrompt += `\nOpdracht: Haal EERST expliciet de FUNCTIE-EISEN (knock-out eisen) uit de vacaturetekst. Geef dan toelichting op al deze gevonden functie-eisen per eis in de ik-vorm (instructie 2). Geef in de toelichting voorbeelden van werkzaamheden en functies en bedrijven waarvoor hij/zij heeft gewerkt (als eerste overheidsinstanties noemen indien mogelijk). Maak geen hele lange zinnen. Gebruik voor het beantwoorden onderstaand de eigen toelichting, deze is leidend. Vul waar nodig aan met het cv.`;
      } else if (mode === 'wensen') {
          userPrompt += `\nOpdracht: Haal EERST expliciet de WENSEN (nice-to-haves, geen harde eisen) uit de vacaturetekst. Geef dan toelichting op al deze gevonden wensen per wens in de ik-vorm (instructie 2). Geef in de toelichting voorbeelden van werkzaamheden en functies en bedrijven waarvoor hij/zij heeft gewerkt (als eerste overheidsinstanties noemen indien mogelijk). Maak geen hele lange zinnen. Gebruik voor het beantwoorden onderstaand de eigen toelichting, deze is leidend. Vul waar nodig aan met het cv.`;
      } else if (mode === 'motivatie') {
          userPrompt += `\nOpdracht: Schrijf 2 alinea's voor in een motivatiebrief (instructie 3). Geen lange zinnen en begin soms een zin met "ik", maar nooit de eerste zin van de alinea. Beschrijf in de eerste alinea waarom hij de rol zo graag wil en zijn eerdere ervaring (benoem de overheden en relevante functies) en in de tweede alinea (beginnend met "Wat mij tekent als persoon…") wat meer van zijn karaktereigenschappen. Gebruik voor de motivatie de eigen toelichting, deze is leidend. Vul waar nodig aan met het cv. Noem eerst zijn aanleiding/affiniteit met de opdracht en noem de rol en bedrijf pas in de 3e zin. Voeg geen headers boven de alinea's.`;
      } else if (mode === 'expertise') {
          userPrompt += `\nOpdracht: Welke expertisegebieden en welke competenties heeft hij die passen bij de rol? (instructie 4). Zet ze onder elkaar in bulletpoints zonder extra uitleg.`;
      } else {
          userPrompt += `\nOpdracht: Retourneer de toelichting op de functie-eisen, motivatie en expertisegebieden/competentie bullet list allemaal in één groot bericht gebruikmakende van de regels.`;
      }
  }

  // --- Provider Fallbacks ---
  if (provider === 'chatgpt' && openaiKey) {
      try {
          const res = await fetch('https://api.openai.com/v1/chat/completions', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${openaiKey}`
              },
              body: JSON.stringify({
                  model: 'gpt-4o',
                  messages: [
                      { role: 'system', content: activeSystemInstruction },
                      { role: 'user', content: userPrompt }
                  ],
                  temperature: 0.7
              })
          });
          if (!res.ok) throw new Error(`OpenAI error: ${await res.text()}`);
          const data = await res.json();
          return data.choices[0].message.content;
      } catch (e: any) {
          console.error("OpenAI direct API failed, falling back to Azure proxy", e);
      }
  }

  const response = await ai.models.generateContent({
      model: AI_PROXY_CHAT_MODEL,
      contents: activeSystemInstruction + "\n\n" + userPrompt
  });
  return response.text;
};

export const generateIndustryName = async (companyName: string, sbiDescription: string) => {
  try {
      const prompt = `Je bent een data verrijking assistent voor een modern recruitment CRM.\nWe hebben een bedrijf genaamd '${companyName}'. De officiële KVK SBI-omschrijving is: '${sbiDescription}'.\nEen SBI-code is vaak erg formeel of abstract (bijv. 'OVERIGE ACTIVITEITEN VAN HOOFDKANTOREN').\nJouw taak is om dit te vertalen naar een korte, algemeen begrepen Nederlandse branche- of industrienaam. (bijvoorbeeld: 'Netbeheerder', 'IT Dienstverlening', 'Bouw & Infra', 'Financiële Dienstverlening').\n\nGeef ALLEEN de korte industrienaam terug. Geen interpunctie, geen uitleg, geen inleiding. Maximaal 2 of 3 woorden.`;
      const response = await ai.models.generateContent({ model: AI_PROXY_CHAT_MODEL, contents: prompt });
      return (response.text || "").trim();
  } catch (e) {
      console.error('Error generating industry name:', e);
      return sbiDescription;
  }
};

export const generateEisenWensenMatches = async (
    cvData: any,
    vacancy: any,
    mode: 'eisen' | 'wensen',
    clientId?: string
): Promise<{ titel: string; toelichting: string }[]> => {
    let customPrompt = '';
    let provider = 'azure';
    let openaiKey = '';

    if (clientId) {
        const settings = await supabase_get_client_settings(clientId);
        if (settings) {
            const apiSettings = settings.ai_api_settings || {};
            const promptsSettings = settings.ai_prompts_settings || {};

            let promptKey = mode === 'eisen' ? 'buddyEisen' : 'buddyWensen';

            if (promptKey) {
                customPrompt = promptsSettings[promptKey] || promptsSettings['buddyEisen'] || '';
                const specificProvider = promptsSettings[`${promptKey}Provider`];
                if (specificProvider && specificProvider !== 'default') {
                    provider = specificProvider;
                } else {
                    provider = apiSettings.provider || 'azure';
                }
            }
            openaiKey = apiSettings.chatgpt || '';
        }
    }

    const modeLabel = mode === 'eisen' ? 'FUNCTIE-EISEN (knock-out eisen)' : 'WENSEN (nice-to-haves, geen knock-outs)';
    
    let activeSystemInstruction = `Je bent een AI module in een Recruitment applicatie. Jouw taak is om JSON te genereren.`;
    
    let userPrompt = `Opdracht: Haal EERST alle ${modeLabel} uit de vacaturetekst. 
Check vervolgens in het CV van de kandidaat of deze voldoet aan elke specifieke ${mode.substring(0, mode.length - 1)}.
Schrijf vervolgens de toelichting / onderbouwing voor deze kandidaat aan de hand van de volgende tone-of-voice instructies:
"${customPrompt || 'Geef een korte toelichting op basis van het CV.'}"

Antwoord ALTIJD en UITSLUITEND in EXACT het volgende JSON formaat:
{
  "matches": [
    { "titel": "Naam van de eis/wens (kort)", "toelichting": "Jouw geschreven toelichting"}
  ]
}

Context:
Vacature: ${JSON.stringify(vacancy?.description || vacancy)}
Kandidaat CV: ${JSON.stringify(cvData.personalInfo)} \n ${JSON.stringify(cvData.workHistory)} \n ${JSON.stringify(cvData.education)} \n ${JSON.stringify(cvData.course)} \n ${JSON.stringify(cvData.skills)}`;

    const parseJSONResponse = (text: string) => {
        try {
            const match = text.match(/\{[\s\S]*\}/);
            if (match) {
                const parsed = JSON.parse(match[0]);
                return parsed.matches || [];
            }
            return [];
        } catch (e) {
            console.error("Failed to parse JSON array", e);
            return [];
        }
    };

    if (provider === 'chatgpt' && openaiKey) {
        try {
            const res = await fetch('https://api.openai.com/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${openaiKey}`
                },
                body: JSON.stringify({
                    model: 'gpt-4o',
                    messages: [
                        { role: 'system', content: activeSystemInstruction },
                        { role: 'user', content: userPrompt }
                    ],
                    temperature: 0.2,
                    response_format: { type: "json_object" }
                })
            });
            if (!res.ok) throw new Error(`OpenAI error: ${await res.text()}`);
            const data = await res.json();
            return parseJSONResponse(data.choices[0].message.content);
        } catch (e: any) {
            console.error("OpenAI direct API failed, falling back to Azure proxy", e);
        }
    }

    try {
        const response = await ai.models.generateContent({
            model: AI_PROXY_CHAT_MODEL,
            contents: activeSystemInstruction + "\n\n" + userPrompt
        });
        return parseJSONResponse(response.text || "");
    } catch(e) {
        console.error(e);
        return [];
    }
};
